/**
 * canvas-video-export.js — shared hardware video export for any tool that
 * renders frames to a canvas (audio-to-video, slideshow-maker, …).
 *
 * Exposed as `window.CanvasVideoExport`. `window.A2VWebCodecsExport` is kept
 * as a back-compat alias for the original audio-to-video caller.
 *
 *   caller renders each frame to a canvas
 *      → VideoFrame(canvas)
 *      → VideoEncoder (hardware H.264 / VP9)        ─┐
 *      → AudioEncoder (AAC-LC / Opus) from AudioBuffer ┼→ mp4-muxer / webm-muxer
 *        (AAC goes through AacAccessUnit — public/js/aac-access-unit.js — the
 *        door that strips the ADTS framing Chromium 109–111 put on every
 *        chunk and guarantees the esds AudioSpecificConfig; report #6990)
 *                                                    ─┘   → MP4 / WebM Blob
 *
 * Why this exists — replaces the FFmpeg-WASM canvas pipeline, which had
 * defects diagnosed from feedback #451 (Алексей, 2026-05-18):
 *
 *   1. Frames were round-tripped through JPEG (q=0.85) before FFmpeg
 *      re-encoded them — double-lossy. JPEG's 8×8 DCT + 4:2:0 chroma
 *      subsampling mangles the synthetic content audio-to-video produces
 *      (flat fills, sharp spectrum bars, crisp text). WebCodecs encodes
 *      the canvas pixels directly — the ONLY lossy step is the final
 *      hardware video encode, as it should be.
 *
 *   2. libx264-WASM ran at ~3-5 fps on mobile → multi-minute waits + the
 *      685/30d abandon cluster. Hardware encode runs 5-20× faster.
 *
 *   3. webm canvas exports were silently broken: the FFmpeg path encoded
 *      H.264 chunks then concat-copied them into a .webm container — an
 *      invalid combination that produced a 0-byte file. Routing webm
 *      through VP9 + webm-muxer here fixes it properly.
 *
 * Same VideoEncoder → muxer architecture screen-recorder and
 * webcodecs-pipeline.js already use; both muxers are vendored.
 *
 * API — window.CanvasVideoExport:
 *
 *   await isSupported({ width, height, fps, format })
 *     → { ok:true, hardware:bool, videoCodec:'…' }
 *     | { ok:false, reason:'no-webcodecs' | 'no-muxer' | 'no-encoder' | 'bad-format' }
 *
 *   await encode({
 *     width, height, fps, durationSec,
 *     format: 'mp4' | 'webm',          // default 'mp4'
 *     drawFrame: (ctx2d, frameIndex, timeSec) => void,
 *     audioBuffer: AudioBuffer | null,
 *     qualityCrf: 18 | 23 | 28,        // mapped to a VBR bitrate
 *     signal: AbortSignal,             // optional — cancellation
 *     onProgress: (frac0to1) => void,  // optional
 *     frameTimes: number[]             // optional — seconds, ascending: frame i
 *                                      // is shown at frameTimes[i] until the next
 *                                      // one (the last until durationSec). The
 *                                      // file keeps a source's own frame times;
 *                                      // without it — round(durationSec·fps)
 *                                      // frames at i/fps
 *   })  → Blob
 *
 * Dependencies: WebCodecs natively in the browser; window.Mp4Muxer
 * (/vendor/mp4-muxer/) and window.WebMMuxer (/vendor/webm-muxer/).
 */
(function () {
'use strict';

// ── Уступить поток так, чтобы скрытая вкладка не останавливала экспорт ──────
// (25.09.2026) Кодирование уступало поток таймером — setTimeout(0) каждые 16
// кадров и setTimeout(4) в ожидании очереди кодера. В скрытой вкладке Chrome
// будит таймеры раз в секунду, после 5 минут скрытия — раз в минуту: 16 кадров
// на пробуждение, и долгий экспорт, пока человек смотрит другую вкладку, стоял.
// То же в webcodecs-pipeline.js (см. его _yieldTick, там же замер). Видимая
// вкладка — прежний таймер; скрытая — задача через MessageChannel.
var _tickChan = null, _tickQ = [];
function _yieldTick(ms) {
    if (typeof document === 'undefined' || !document.hidden || typeof MessageChannel === 'undefined') {
        return new Promise(function (r) { setTimeout(r, ms || 0); });
    }
    if (!_tickChan) {
        _tickChan = new MessageChannel();
        _tickChan.port1.onmessage = function () { var f = _tickQ.shift(); if (f) f(); };
    }
    return new Promise(function (r) { _tickQ.push(r); _tickChan.port2.postMessage(0); });
}
/* ── wait for the encoder to take frames off its queue ────────────────────────
   (29.09.2026) The one hot wait the 25.09 fix above missed: the frame loop
   polled `encodeQueueSize` with a bare `setTimeout(check, 4)`, and on a machine
   without a hardware H.264 encoder the ENCODER is the slow side, so the loop
   sits in this wait on nearly every frame. Measured in real Chrome 154 with
   `--disable-accelerated-video-encode`, 30-s deck at 720p: 2208 polls in a
   visible tab (27 s), and in a hidden one each poll took a whole second — the
   same deck ran 110 s, and past 5 minutes hidden Chrome wakes such a chain once
   a MINUTE. The encoder announces every frame it takes (`dequeue`, Chrome 106+,
   Safari 16.4+, Firefox 125+), so the wait is an event, not a poll, visible or
   not. Without the event (Chromium 104/105 — Yandex 22.x is one) it polls
   through _yieldTick, which the background does not throttle. The slow timer
   is only a net for an encoder that dies without a last `dequeue`. */
function waitEncoderQueue(enc, low, stop) {
    return new Promise(function (resolve) {
        var done = false, net = null;
        var evt = !!(enc && ('ondequeue' in enc) && typeof enc.addEventListener === 'function');
        function fin() {
            if (done) return;
            done = true;
            clearTimeout(net);
            if (evt) { try { enc.removeEventListener('dequeue', check); } catch (_) {} }
            resolve();
        }
        function check() {
            if (done) return true;
            if (stop() || !enc || enc.state === 'closed' || enc.encodeQueueSize <= low) { fin(); return true; }
            return false;
        }
        if (check()) return;
        if (evt) {
            enc.addEventListener('dequeue', check);
            (function slow() { if (!check()) net = setTimeout(slow, 1000); })();
        } else {
            (function poll() { if (!check()) _yieldTick(4).then(poll); })();
        }
    });
}
if (window.CanvasVideoExport) return;

/* ───────────────────────── Firefox avcC repair (#458) ─────────────────────────
   Firefox's H.264 VideoEncoder emits a malformed avcC on the first chunk's
   decoderConfig.description: each SPS/PPS NAL gets a STRAY duplicate NAL-header
   byte ([0x67][0x67][profile…] instead of [0x67][profile…]). ffmpeg flags it
   as "sps_id N out of range" / "crop values invalid" and stricter decoders
   reject it. Ported verbatim from webcodecs-pipeline.js sanitizeAvcC — detect
   the duplicate-header signature via SPS[0] and strip it; clean streams (Chrome,
   Safari) are returned untouched (no-op). */
function sanitizeAvcC(desc) {
    try {
        var b = desc instanceof Uint8Array ? desc : new Uint8Array(desc.buffer || desc);
        if (b.length < 7 || b[0] !== 1) return desc;        // not an avcC
        var avcProfile = b[1];
        var sps = [], pps = [], p = 6, i;
        var numSps = b[5] & 0x1F;
        for (i = 0; i < numSps && p + 2 <= b.length; i++) {
            var len = (b[p] << 8) | b[p + 1]; p += 2;
            if (p + len > b.length) return desc;
            sps.push(b.subarray(p, p + len)); p += len;
        }
        if (p >= b.length || !sps.length) return desc;
        var numPps = b[p++];
        for (i = 0; i < numPps && p + 2 <= b.length; i++) {
            var plen = (b[p] << 8) | b[p + 1]; p += 2;
            if (p + plen > b.length) return desc;
            pps.push(b.subarray(p, p + plen)); p += plen;
        }
        var s0 = sps[0];
        var bug = s0.length >= 3 && (s0[0] & 0x1F) === 7 &&
                  s0[1] !== avcProfile && s0[2] === avcProfile;
        if (!bug) return desc;                               // clean — untouched
        function strip(nal) {
            return (nal.length >= 2 && nal[1] === nal[0]) ? nal.subarray(1) : nal;
        }
        sps = sps.map(strip); pps = pps.map(strip);
        var size = 7, o = 0;
        sps.forEach(function(n){ size += 2 + n.length; });
        pps.forEach(function(n){ size += 2 + n.length; });
        var out = new Uint8Array(size);
        out[o++] = 1; out[o++] = b[1]; out[o++] = b[2]; out[o++] = b[3];
        out[o++] = b[4]; out[o++] = 0xE0 | (sps.length & 0x1F);
        sps.forEach(function(n){
            out[o++] = (n.length >> 8) & 0xFF; out[o++] = n.length & 0xFF;
            out.set(n, o); o += n.length;
        });
        out[o++] = pps.length & 0xFF;
        pps.forEach(function(n){
            out[o++] = (n.length >> 8) & 0xFF; out[o++] = n.length & 0xFF;
            out.set(n, o); o += n.length;
        });
        return out.buffer;
    } catch (e) { return desc; }
}

/* ───────────────────────── container specs ─────────────────────────
   Each output container pairs a muxer, a video codec family and an audio
   codec. mp4 → H.264 + AAC; webm → VP9 + Opus. */
var CONTAINERS = {
    mp4: {
        muxerGlobal: 'Mp4Muxer',
        muxerSrc: '/vendor/mp4-muxer/mp4-muxer.js',
        muxerVideoCodec: 'avc',
        muxerAudioCodec: 'aac',
        mime: 'video/mp4',
        videoProfiles: ['avc1.640028', 'avc1.4d4028', 'avc1.42e028', 'avc1.640033', 'avc1.4d4033'],
        audioCodec: 'mp4a.40.2',
        encoderExtra: { avc: { format: 'avc' } },
        // mp4-muxer REQUIRES fastStart; 'in-memory' writes the moov atom at
        // the file head so the MP4 plays everywhere (incl. Win11 Movies & TV).
        // Omitting it throws "'fastStart' option must be …" — which silently
        // bounced every mp4 export to the FFmpeg fallback (caught in the
        // 2026-05-19 scenario-matrix run).
        muxerExtra: { fastStart: 'in-memory' }
    },
    webm: {
        muxerGlobal: 'WebMMuxer',
        muxerSrc: '/vendor/webm-muxer/webm-muxer.min.js',
        muxerVideoCodec: 'V_VP9',
        muxerAudioCodec: 'A_OPUS',
        mime: 'video/webm',
        videoProfiles: ['vp09.00.10.08', 'vp09.00.30.08', 'vp8'],
        audioCodec: 'opus',
        encoderExtra: {},
        // webm is inherently streamable — webm-muxer has no fastStart option.
        muxerExtra: {}
    }
};

/* The container label must follow the codec the ladder actually picked. The
   WebM ladder ends in VP8, and until 13.09.2026 a VP8 stream was written under
   the container's default `V_VP9`: every decoder read it as VP9 and gave up on
   the first frame («Invalid frame marker» on all of them — measured on
   /audio-to-video's VP9 lane, whose FFmpeg finish died on such a file). The
   file played in nothing. Only WebM has two codec families behind one label;
   mp4 here is always AVC. */
function muxVideoCodecFor(spec, vc) {
    if (spec && spec.muxerGlobal === 'WebMMuxer' && vc && /^vp8\b/i.test(vc.codec || '')) return 'V_VP8';
    // mp4 carrying the VP9 rescue (pickVideoCodec): mp4-muxer's own vp09 sample entry.
    if (spec && spec.muxerGlobal === 'Mp4Muxer' && vc && /^vp09\./i.test(vc.codec || '')) return 'vp9';
    return spec.muxerVideoCodec;
}

/* ───────────────────────── muxer loader ───────────────────────── */
/* ── every setup wait before the first frame is BOUNDED (2026-09-17) ───────
   Report #16.09 (Edge 153, 2 cores / 4 GB): both WebCodecs lanes produced ZERO
   ticks for 180 s each — the encode loop never finished frame 0 — and the only
   awaits between `frame_render` and the first beat() are these: the muxer and
   AAC-door <script>s, the OPFS handle, the codec probes. None had a bound, so
   one wedged browser subsystem (a stuck service worker, a locked storage
   backend, a hung GPU process) cost the user 2 × 180 s of the stall guard
   before the lane even failed, with «Encoding is slow on your device» as the
   only word. A wait that publishes no progress is judged by a deadline; the
   deadline names its phase so the fallback reason of the lane says WHAT hung. */
var SETUP_TIMEOUTS = {
    script: 20000,    // 32 KB muxer: under 13 kbit/s is not a working link; two attempts inside loadOnce
    opfs: 10000,      // storage backend answers in ms; a wedge falls to the in-memory target
    picked: 45000,    // a file the person picked: any disk, incl. a USB stick; the lane gives up its own reservation at 15 s
    probe: 5000,      // isConfigSupported goes to the GPU process; a hung one is "unsupported"
    capture: 15000    // whole capture probe incl. VideoDecoder.isConfigSupported + copyTo
};
function withTimeout(promise, ms, label) {
    var t = null;
    var deadline = new Promise(function (_, rej) {
        t = setTimeout(function () {
            var e = new Error('timeout:' + label + ' (' + Math.round(ms / 1000) + 's)');
            e.timedOut = true; e.setupPhase = label;
            rej(e);
        }, ms);
    });
    return Promise.race([promise, deadline]).finally(function () { clearTimeout(t); });
}
function loadScript(src) {
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        var timer = setTimeout(function () {
            try { s.remove(); } catch (_) {}
            var e = new Error('timeout:script ' + src + ' (' + Math.round(SETUP_TIMEOUTS.script / 1000) + 's)');
            e.timedOut = true; e.setupPhase = 'script';
            reject(e);
        }, SETUP_TIMEOUTS.script);
        s.src = src;
        s.onload = function () { clearTimeout(timer); resolve(); };
        s.onerror = function () { clearTimeout(timer); try { s.remove(); } catch (_) {} reject(new Error('load: ' + src)); };
        document.head.appendChild(s);
    });
}
/* A helper this module fetches at export time is a DEPENDENCY OF THE FAST LANE,
   so a single dropped request must not decide the lane. Measured 15.09.2026 in
   real Opera 135: one ERR_CONNECTION_RESET on the 3 KB AAC helper failed the
   WebCodecs export, the tool fell to the per-frame PNG lane (3 min here, tens of
   minutes on an ordinary PC) — for a browser that encodes H.264 fine. Two rules:
     • try again once, on a URL the browser cannot answer from a failed attempt;
     • never CACHE a refusal: the loading promise is kept only while it can still
       succeed, otherwise every later export on the page inherited the rejection
       and the fast lane stayed dead until a reload. */
function loadOnce(key, urls, ready) {
    if (ready()) return Promise.resolve();
    if (window[key]) return window[key];
    var attempt = function (i) {
        return loadScript(urls[i]).then(function () {
            // A 200 that defines nothing (truncated by a proxy, an HTML error
            // page) is the same failure as a refused request: try the next URL.
            if (!ready()) throw new Error('loaded-without-global: ' + urls[i]);
        }).catch(function (e) {
            if (i + 1 >= urls.length) throw e;
            return new Promise(function (r) { setTimeout(r, 400); }).then(function () { return attempt(i + 1); });
        });
    };
    window[key] = attempt(0).catch(function (e) { window[key] = null; throw e; });
    return window[key];
}
function retryUrl(src) {
    return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'retry=1';
}
async function ensureMuxer(spec) {
    if (window[spec.muxerGlobal]) return;
    await loadOnce('__muxerLoading_' + spec.muxerGlobal, [spec.muxerSrc, retryUrl(spec.muxerSrc)],
        function () { return !!window[spec.muxerGlobal]; });
}
/* AacAccessUnit — the one door for AAC on its way into an MP4 track (strips
   the ADTS framing Chromium 109–111 emitted, guarantees the esds ASC). Loaded
   like the muxer: at runtime, versioned through __jsSrc (layouts/app.blade.php)
   so a returning visitor is not served a year-old copy. */
async function ensureAacAccessUnit() {
    if (window.AacAccessUnit) return;
    var primary = typeof window.__jsSrc === 'function' ? window.__jsSrc('/js/aac-access-unit.js') : '/js/aac-access-unit.js';
    await loadOnce('__aacAccessUnitLoading', [primary, retryUrl('/js/aac-access-unit.js?v=' + (window.__JS_VER || ''))],
        function () { return !!window.AacAccessUnit; });
}

/* ── H.264 from WebAssembly when the browser has none (2026-10-29) ────────
   Yandex Browser ships no OpenH264: on a machine without a hardware H.264
   encoder its WebCodecs and MediaRecorder both refuse avc1, and until today
   this file answered with VP9 inside the .mp4 (pickVideoCodec below) — a file
   iPhone, WhatsApp and Windows' own player do not take — while /slideshow-maker
   went VP9 + an FFmpeg-WASM transcode that peaked at 4.1 GB. WasmH264Encoder
   (wasm-h264-encoder.js) is libx264 in a worker behind the VideoEncoder
   interface, so every loop below runs it unchanged; `WASM_HINT` is its rung in
   the hint ladder and `newVideoEncoder` the one place an encoder is made.
   Measured, why x264 and not OpenH264/minih264 — docs/why/primitives/wasm-h264.md. */
var WASM_HINT = 'wasm';
var WASM_PROBE_MS = 30000;    // first use downloads ~690 KB of wasm and compiles it
async function ensureWasmH264() {
    if (!window.WasmH264Encoder) {
        var primary = typeof window.__jsSrc === 'function' ? window.__jsSrc('/js/wasm-h264-encoder.js') : '/js/wasm-h264-encoder.js';
        try {
            await loadOnce('__wasmH264Loading', [primary, retryUrl('/js/wasm-h264-encoder.js?v=' + (window.__JS_VER || ''))],
                function () { return !!window.WasmH264Encoder; });
        } catch (_) { return false; }
    }
    return !!(window.WasmH264Encoder && window.WasmH264Encoder.isSupported());
}
/* The rung at THIS size: above 1080p only where x264 can run threaded
   (WasmH264Encoder.suits — measured speeds in its header). */
async function wasmH264Suits(width, height) {
    return (await ensureWasmH264()) && window.WasmH264Encoder.suits(width, height);
}
function newVideoEncoder(init, hint) {
    if (hint === WASM_HINT) return new window.WasmH264Encoder(init);
    // the caller's callbacks, forwarded — every call site is linted for its own
    // error handler (WebCodecsErrorCallbackTest)
    return new VideoEncoder({ output: init.output, error: function (e) { init.error(e); } });
}

/* ───────────────────────── codec selection ─────────────────────────
   Probe codec profiles best→fallback, hardware first. */
/* ───────────────────────── real encoder probe ─────────────────────────
   ASK THE ENCODER THAT WILL ACTUALLY RUN. `isConfigSupported` is advisory and
   lies in BOTH directions: it says yes on machines whose H.264 accelerator
   cannot be initialised, and no on machines that encode fine. The code below
   used to work around the second lie by attempting an unverified config; there
   was no answer to the first, and the first is the expensive one — the tool
   renders frames into a doomed encoder, watches it die, walks the hint
   cascade, and only then falls back.

   Measured on report #6673 (tasya1983, 08.09.2026, YaBrowser 24.10 / Chromium
   128, no GPU adapter): five exports, zero files. Each MP4 attempt burned ~30 s
   on `avc1` — three hints, dying at frame 14 with «Failed to initialize video
   encode accelerator» — before falling to WebM, where VP9 encoded perfectly.
   `isConfigSupported` had said yes to H.264 every time, so the pre-flight that
   would have OFFERED her WebM up front (`checkFastPath` → slow-path dialog)
   never fired: `slow_path:0` on all five.

   The probe encodes ONE 64×64 frame and waits for a chunk to come out. That is
   the only statement that cannot be wrong: bytes came back. Cached per
   (codec, hint) for the page, because the answer is a property of the machine.

   ⚠️ A tiny frame is not a free pass for every size — hardware encoders have
   minimum dimensions (see CodecMatrix's note on decoders) — so a probe FAILURE
   is trusted, and a probe SUCCESS still leaves the existing cascade in place
   downstream. The probe removes the wait, not the safety net. */
/* ── ONE encoder config — the probe asks what the export will run (2026-10-30) ──
   The probe used to encode its frame at 30 fps and a token bitrate; the export
   then configured the encoder at the project's fps, its own bitrate,
   bitrateMode 'variable' and latencyMode 'realtime'. A hardware H.264 encoder
   has a frame-rate ceiling per size, so the two are different questions.
   Measured in Yandex Browser 26.8 (the report's build) on an RTX 4060: above
   1920×1088 the probe's 30 fps encodes and the export's 60 fps does not —
   isConfigSupported(real) says no, configure() dies with «Encoder creation
   error» on prefer-hardware and «Encoder initialization error (Failed to create
   OpenH264 encoder)» on no-preference, because Yandex ships no OpenH264. The
   report of 23.10 (comment #1990, /video-editor, «не компилирует, вечные
   ошибки») is the same death at 720p60 on a weaker encoder: fifteen builds in
   two hours, every one a toast; 39 /video-editor sessions in October, 13 of
   them never got a file. The ladder decided «H.264 works» on a question it
   never asked again. Now every probe and every rung of the export are built by
   this one function, so a verdict is a verdict about the encoder that runs.
   `software` — the rung is the software encoder (isSoftwareRung): its own
   bits (swRungBps) and its own rate rule (applyEncoderRate). */
function encoderConfig(spec, codec, hint, w, h, fps, bitrate, o, software) {
    if (hint === WASM_HINT) {
        /* x264 takes the bitrate at face value: none of the hardware/OpenH264
           corrections below (rate rewrite, software-rung budget) apply. */
        return { codec: codec, width: w, height: h, bitrate: bitrate, framerate: fps };
    }
    var sw = software ? swRungBps(bitrate, w, h, fps, o, codec) : 0;
    var cfg = {
        codec: codec, width: w, height: h,
        bitrate: sw || bitrate, framerate: fps,
        bitrateMode: 'variable',
        /* latencyMode:'realtime' forbids B-frames. Without it, the encoder
           may emit chunks out of presentation order (H.264 reordering),
           which needs an explicit compositionTimeOffset on the muxer side
           — the same trap enhance-video hit in 68256c93b. Our frames are
           monotonic and we encode at faster-than-realtime; B-frames buy
           nothing here. mp4-muxer rejects out-of-order DTS with
           "Timestamps must be monotonically increasing" (1 telemetry event
           / 14 d) — this turns that error class off at the source. The
           sibling screen-recorder uses the same setting. */
        latencyMode: 'realtime'
    };
    if (hint) cfg.hardwareAcceleration = hint;
    if (/^avc1\./i.test(codec || '') && spec && spec.encoderExtra) Object.assign(cfg, spec.encoderExtra);   // avc: {format} is H.264-only
    applyEncoderRate(cfg, software);
    return cfg;
}

var _encProbe = Object.create(null);
/* `real` — { spec, fps, bitrate, o, software }: the export's own numbers, so
   the frame goes through encoderConfig exactly as the export's will. Without
   it (an outside caller asking «can this machine make the codec at all») the
   question is asked at 30 fps and a medium bitrate. */
async function probeEncoder(codec, hint, width, height, real) {
    /* At the REAL export size, not a token 64×64. Hardware encoders have
       minimum dimensions — the same trap CodecMatrix documents for decoders,
       where a 64×64 fixture «proved» that Chrome has no HEVC — so a small
       probe can refuse a configuration that would have worked. Probing the
       configuration we are about to use removes the caveat instead of
       reasoning around it, and costs the same milliseconds. */
    var w = Math.max(16, Math.round(width || 640));
    var h = Math.max(16, Math.round(height || 360));
    var r = real || {};
    var fps = r.fps > 0 ? r.fps : 30;
    var cfg = encoderConfig(r.spec || CONTAINERS.mp4, codec, hint || null, w, h, fps,
        r.bitrate > 0 ? r.bitrate : videoBitrateFor(w, h, fps, 23), r.o || null, !!r.software);
    var key = (hint || 'none') + '|' + JSON.stringify(cfg);
    if (key in _encProbe) return _encProbe[key];
    _encProbe[key] = (async function () {
        if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') return false;
        var enc = null, got = false;
        try {
            await new Promise(function (resolve, reject) {
                var settled = false;
                var done = function (ok) { if (settled) return; settled = true; got = ok; resolve(); };
                try {
                    enc = newVideoEncoder({
                        output: function () { done(true); },
                        error: function () { done(false); },
                    }, hint);
                    enc.configure(cfg);
                    var c = (typeof OffscreenCanvas !== 'undefined')
                        ? new OffscreenCanvas(w, h) : document.createElement('canvas');
                    c.width = w; c.height = h;
                    var g = c.getContext('2d');
                    g.fillStyle = '#345'; g.fillRect(0, 0, w, h);
                    var vf = new VideoFrame(c, { timestamp: 0, duration: 33333 });
                    enc.encode(vf, { keyFrame: true });
                    vf.close();
                    enc.flush().then(function () { done(got); }, function () { done(false); });
                } catch (_) { done(false); }
                // A wedged GPU process can leave configure() hanging; a probe
                // that never answers is a probe that costs what it was meant
                // to save. The wasm rung's first answer includes its download.
                setTimeout(function () { done(false); }, hint === WASM_HINT ? WASM_PROBE_MS : 4000);
            });
        } catch (_) { got = false; }
        try { if (enc && enc.state !== 'closed') enc.close(); } catch (_) {}
        return got;
    })();
    return _encProbe[key];
}

/* ───────────────── capture probe: do the PIXELS make it? ─────────────────
   A frame can reach the encoder with nothing in it. User 6673 (09.09.2026,
   YaBrowser 24.10, gpu:1 but gpuad:0 / gpudv:0 — a WebGL context with no GPU
   adapter behind it): her preview played, her export decoded cleanly to 6723
   frames of Y=U=V=0 — a buffer no converter ever wrote, which every player
   shows as solid green. The encoder was fine (probeEncoder passed), the codec
   was fine, the muxer was fine. What died was canvas → VideoFrame: the readback
   from a GPU-backed surface on a machine whose GPU path is broken hands the
   encoder zeros, and nothing downstream can tell.

   probeEncoder asks «does this encoder emit bytes». This asks the next
   question: «do the bytes carry the picture». One pattern frame is encoded and
   DECODED BACK, and the Y plane is measured. Calibrated 09.09.2026 on both
   Playwright Chromium and Yandex, every codec × hint: a live pattern decodes to
   Y≈129, a dead buffer to Y≤16 — threshold 64, seventy points of margin each
   way. Chunk SIZE was tried first and rejected: 763 vs 376 bytes on VP9 and
   2062 vs 1988 on H.264 do not separate.

   The fallback is a CPU-backed surface (willReadFrequently:true), whose
   readback is a memcpy that owes the GPU nothing. It is NOT the default:
   measured on a healthy GPU it draws a scaled photo in 65 ms instead of 25 —
   2.6× slower for every export on every machine, to fix a failure that lives on
   a few. So: ask first, fall back only where the answer says so, and when both
   surfaces are dead say so in a second instead of after three minutes. Cached
   per codec|hint|size — it is a property of the machine. */
var CAPTURE_Y_ALIVE = 64;
var _capProbe = {};

function makeSurface(w, h, cpu) {
    var c = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(w, h) : document.createElement('canvas');
    c.width = w; c.height = h;
    var g = c.getContext('2d', cpu ? { willReadFrequently: true } : undefined);
    return { canvas: c, ctx: g };
}

function paintProbePattern(g, w, h) {
    // Mid-grey with a light and a dark block: Y≈129 through any encoder.
    g.fillStyle = 'rgb(118,118,118)'; g.fillRect(0, 0, w, h);
    g.fillStyle = 'rgb(200,200,200)'; g.fillRect(w * 0.25, h * 0.25, w * 0.5, h * 0.5);
    g.fillStyle = 'rgb(40,40,40)';    g.fillRect(w * 0.4,  h * 0.4,  w * 0.2, h * 0.2);
}

/* Mean of the decoded frame's luma over a sparse grid. */
async function decodedLuma(frame, w, h) {
    var buf = new Uint8Array(frame.allocationSize());
    var layout = await frame.copyTo(buf);
    var fmt = String(frame.format || '');
    var sum = 0, n = 0;
    if (/^(I420|I422|I444|NV12|I420A)/.test(fmt) && layout && layout[0]) {
        var off = layout[0].offset, stride = layout[0].stride;
        for (var y = 0; y < h; y += 8) for (var x = 0; x < w; x += 8) { sum += buf[off + y * stride + x]; n++; }
    } else {
        for (var i = 0; i + 2 < buf.length; i += 4 * 64) { sum += (buf[i] + buf[i + 1] + buf[i + 2]) / 3; n++; }
    }
    return n ? sum / n : 0;
}

/* One frame through the REAL path — pattern → surface → VideoFrame → encoder →
   decoder → luma. `judged:false` means the probe could not form an opinion (no
   decoder for this codec, timeout); that is fail-open, never a verdict. */
function probeCapture(codec, hint, w, h, cpu, real) {
    var cfg = captureConfig(codec, hint, w, h, real);
    var key = (hint || 'none') + '|' + JSON.stringify(cfg) + '|' + (cpu ? 'cpu' : 'gpu');
    if (key in _capProbe) return _capProbe[key];
    /* The inner timeouts cover encode and decode; VideoDecoder.isConfigSupported
       and frame.copyTo were outside them, and a hung GPU process hangs both.
       One outer deadline; a probe that never answers is `judged:false` (fail-open). */
    _capProbe[key] = withTimeout(probeCaptureInner(codec, hint, w, h, cpu, cfg), SETUP_TIMEOUTS.capture, 'capture')
        .catch(function (e) { return { ok: true, judged: false, y: null, ms: SETUP_TIMEOUTS.capture, why: (e && e.timedOut) ? 'probe-timeout' : ('probe-threw:' + String(e && e.message || e).slice(0, 60)) }; });
    return _capProbe[key];
}
/* The capture probe encodes through the same config as the export (see
   encoderConfig) — a frame that cannot be made at the export's rate is not
   evidence about the pixels. */
function captureConfig(codec, hint, w, h, real) {
    var r = real || {};
    var fps = r.fps > 0 ? r.fps : 30;
    return encoderConfig(r.spec || CONTAINERS.mp4, codec, hint || null, w, h, fps,
        r.bitrate > 0 ? r.bitrate : videoBitrateFor(w, h, fps, 23), r.o || null, !!r.software);
}
function probeCaptureInner(codec, hint, w, h, cpu, cfg) {
    return (async function () {
        var now = function () { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); };
        var t0 = now();
        var out = { ok: true, judged: false, y: null, ms: 0, why: '' };
        if (typeof VideoEncoder === 'undefined' || typeof VideoDecoder === 'undefined' || typeof VideoFrame === 'undefined') {
            out.why = 'no-webcodecs'; return out;
        }
        var enc = null, dec = null;
        try {
            var chunk = null, meta = null;
            await new Promise(function (resolve) {
                var settled = false;
                var done = function () { if (settled) return; settled = true; resolve(); };
                try {
                    enc = newVideoEncoder({
                        output: function (c, m) { if (!chunk) { chunk = c; meta = m; } },
                        error: function () { done(); },
                    }, hint);
                    enc.configure(cfg);
                    var sf = makeSurface(w, h, cpu);
                    if (!sf.ctx) { out.why = 'no-2d'; done(); return; }
                    paintProbePattern(sf.ctx, w, h);
                    var vf = new VideoFrame(sf.canvas, { timestamp: 0, duration: 33333 });
                    enc.encode(vf, { keyFrame: true });
                    vf.close();
                    enc.flush().then(done, done);
                } catch (_) { done(); }
                setTimeout(done, hint === WASM_HINT ? WASM_PROBE_MS : 4000);
            });
            if (!chunk) { out.why = out.why || 'no-chunk'; return out; }
            var bytes = new Uint8Array(chunk.byteLength);
            chunk.copyTo(bytes);
            /* The encoder's own codec string when it states one — the wasm rung
               names its level only after it has seen the frame size. */
            var dcfg = { codec: (meta && meta.decoderConfig && meta.decoderConfig.codec) || codec, codedWidth: w, codedHeight: h };
            if (meta && meta.decoderConfig && meta.decoderConfig.description) dcfg.description = meta.decoderConfig.description;
            var sup = null;
            try { sup = await VideoDecoder.isConfigSupported(dcfg); } catch (_) { sup = null; }
            if (!sup || !sup.supported) { out.why = 'no-decoder'; return out; }
            var frames = [];
            await new Promise(function (resolve) {
                var settled = false;
                var done = function () { if (settled) return; settled = true; resolve(); };
                try {
                    dec = new VideoDecoder({ output: function (f) { frames.push(f); }, error: function () { done(); } });
                    dec.configure(dcfg);
                    dec.decode(new EncodedVideoChunk({ type: 'key', timestamp: 0, data: bytes }));
                    dec.flush().then(done, done);
                } catch (_) { done(); }
                setTimeout(done, 4000);
            });
            if (!frames.length) { out.why = 'no-decoded-frame'; return out; }
            out.y = await decodedLuma(frames[0], w, h);
            out.judged = true;
            out.ok = out.y > CAPTURE_Y_ALIVE;
            if (!out.ok) out.why = 'dead-readback';
            for (var i = 0; i < frames.length; i++) { try { frames[i].close(); } catch (_) {} }
            return out;
        } catch (e) {
            out.why = 'probe-threw:' + String(e && e.message || e).slice(0, 60);
            return out;
        } finally {
            try { if (enc && enc.state !== 'closed') enc.close(); } catch (_) {}
            try { if (dec && dec.state !== 'closed') dec.close(); } catch (_) {}
            out.ms = Math.round(now() - t0);
        }
    })();
}

/* Which surface carries pixels on THIS machine: 'gpu' (the default), 'cpu'
   (willReadFrequently), or null when neither does. */
async function pickCaptureSurface(codec, hint, w, h, real) {
    var gpu = await probeCapture(codec, hint, w, h, false, real);
    if (gpu.ok) return { surface: 'gpu', gpu: gpu, cpu: null };
    var cpu = await probeCapture(codec, hint, w, h, true, real);
    if (cpu.ok) return { surface: 'cpu', gpu: gpu, cpu: cpu };
    return { surface: null, gpu: gpu, cpu: cpu };
}

function reportCapture(verdict, codec, w, h) {
    // Only the interesting outcomes: a healthy machine on its default surface
    // is the norm and would be noise. export_started is the denominator.
    if (!verdict || verdict.surface === 'gpu') return;
    try {
        window.__ae && window.__ae('feature_use', {
            f: 'cve_capture_probe', surface: verdict.surface || 'dead', codec: codec, w: w, h: h,
            y_gpu: verdict.gpu && verdict.gpu.y != null ? Math.round(verdict.gpu.y) : -1,
            y_cpu: verdict.cpu && verdict.cpu.y != null ? Math.round(verdict.cpu.y) : -1,
            why_gpu: verdict.gpu ? verdict.gpu.why : '', why_cpu: verdict.cpu ? verdict.cpu.why : '',
            ms: (verdict.gpu ? verdict.gpu.ms : 0) + (verdict.cpu ? verdict.cpu.ms : 0),
        });
    } catch (_) {}
}

function captureDeadError(verdict) {
    var e = new Error('capture-dead');
    e.captureDead = true;
    e.captureProbe = verdict;
    return e;
}

/* An H.264 codec string names a LEVEL, and a level caps the frame size in
   macroblocks (ITU-T H.264 Table A-1, MaxFS). The ladder tries level 4.0 first
   because that is what every encoder takes up to 1080p — and above 8192
   macroblocks it can never work: Chromium's configure() answers «coded area
   exceeds the maximum supported by the AVC level (4.0)». When every probe had
   refused (a phone's hardware encoder at a portrait 1584×2816), the unverified
   last resort below attempted spec.videoProfiles[0] — level 4.0 — i.e. the one
   string guaranteed to fail (prod /remove-object-from-video, 14.09.2026). The
   ladder now only offers strings whose level can hold the frame; codecs it
   cannot read (VP9, VP8) pass through untouched. */
var AVC_MAX_FS = { 40: 8192, 41: 8192, 42: 8704, 50: 22080, 51: 36864, 52: 36864, 60: 139264, 61: 139264, 62: 139264 };
function avcLevelFits(codec, width, height) {
    var m = /^avc1\.[0-9a-f]{4}([0-9a-f]{2})$/i.exec(String(codec || ''));
    if (!m) return true;
    var maxFs = AVC_MAX_FS[parseInt(m[1], 16)];
    if (!maxFs) return true;
    return Math.ceil(width / 16) * Math.ceil(height / 16) <= maxFs;
}
function profilesFor(spec, width, height) {
    var fit = spec.videoProfiles.filter(function (c) { return avcLevelFits(c, width, height); });
    return fit.length ? fit : spec.videoProfiles;
}

var VP9_IN_MP4 = 'vp09.00.31.08';   // the string the pipeline's enc_vp9_rescue measured
/* The browser's OWN encoder for `spec` at this size and rate, proved by one
   real frame through the config the export will configure (encoderConfig).
   null — none of this browser's encoders makes it. */
async function nativeEncoderAt(spec, width, height, bitrate, fps, o) {
    spec = Object.assign({}, spec, { videoProfiles: profilesFor(spec, width, height) });
    var real = function (software) { return { spec: spec, fps: fps, bitrate: bitrate, o: o, software: software }; };
    for (var i = 0; i < spec.videoProfiles.length; i++) {
        for (var hw = 0; hw < 2; hw++) {
            try {
                /* The two rungs the export starts on: the hardware encoder,
                   and `no-preference` with the software rung's bits (a pick
                   without hardware — isSoftwareRung). */
                var hint = hw === 0 ? 'prefer-hardware' : 'no-preference';
                var cfg = encoderConfig(spec, spec.videoProfiles[i], hint, width, height, fps, bitrate, o, hw === 1);
                var r = await withTimeout(VideoEncoder.isConfigSupported(cfg), SETUP_TIMEOUTS.probe, 'isConfigSupported');
                if (r && r.supported) {
                    /* …and then CHECK it. isConfigSupported is advisory; on the
                       machines in report #6673 it says yes to H.264 while the
                       accelerator cannot be initialised at all, which costs the
                       user 30 s of rendering into a dying encoder before the
                       cascade gives up. One frame settles it. A profile
                       that fails the probe is skipped, not attempted. */
                    if (await probeEncoder(spec.videoProfiles[i], hint, width, height, real(hw === 1))) {
                        return { codec: spec.videoProfiles[i], hardware: hw === 0, config: r.config };
                    }
                }
            } catch (_) { /* try next */ }
        }
    }
    /* Nothing the advisory probe approved could actually encode a frame. Before
       surrendering, ask the probe DIRECTLY for every profile: isConfigSupported
       also lies the other way, and a codec it refused may encode fine. This is
       what finds VP8 on a machine whose VP9 and H.264 are both out. */
    for (var j = 0; j < spec.videoProfiles.length; j++) {
        if (await probeEncoder(spec.videoProfiles[j], 'no-preference', width, height, real(true))) {
            return {
                codec: spec.videoProfiles[j], hardware: false,
                config: { codec: spec.videoProfiles[j], width: width, height: height, bitrate: bitrate, framerate: fps },
                provedByProbe: true,
            };
        }
    }
    return null;
}

async function pickVideoCodec(spec, width, height, bitrate, fps, h264Only, onlyRung, o) {
    if (typeof VideoEncoder === 'undefined' || !VideoEncoder.isConfigSupported) return null;
    var real = function (software) { return { spec: spec, fps: fps, bitrate: bitrate, o: o, software: software }; };
    if (onlyRung === 'vp9') {
        if (spec.muxerGlobal === 'Mp4Muxer' && await probeEncoder(VP9_IN_MP4, 'no-preference', width, height, real(true))) {
            return { codec: VP9_IN_MP4, hardware: false, provedByProbe: true, vp9Rescue: true,
                config: { codec: VP9_IN_MP4, width: width, height: height, bitrate: bitrate, framerate: fps } };
        }
        return null;
    }
    var nat = await nativeEncoderAt(spec, width, height, bitrate, fps, o);
    if (nat) return nat;
    spec = Object.assign({}, spec, { videoProfiles: profilesFor(spec, width, height) });
    /* ── VP9 in the same MP4 when NO H.264 profile encodes a frame (2026-10-08) ──
       Yandex Browser ships no software H.264 encoder at all: OpenH264 is not
       created on any hint (measured, Yandex 26 with hardware encode off: every
       avc1 profile → «Failed to create OpenH264 encoder»; VP9 and VP8 — ok).
       A desktop without a hardware H.264 encoder therefore had no way to
       export here, and the attempt below died on frame 0 every time —
       /reverse-video, 14 days: 36 sessions, 63 % left with nothing. The
       pipeline has carried the same rescue since 24.08 (enc_vp9_rescue: VP9
       was there in 1118 of 1121 sessions where H.264 refused): same codec
       string, same container, AAC untouched. Only after a REAL one-frame
       refusal of every H.264 profile — a lying isConfigSupported decides
       nothing. */
    /* `h264Only` — the caller stitches this part next to byte-copied H.264
       clips (/video-merge normalizing one clip): a VP9 part there would be a
       broken concat, so it keeps today's unverified attempt instead. */
    /* ── H.264 from WebAssembly before VP9 (2026-10-29) ──
       The browser has no H.264 encoder that can encode a frame; ours can. It
       is slower than a hardware encoder and costs ~690 KB once, which is why
       it is a rung BELOW every native H.264 and not above — and above VP9,
       because VP9 in an .mp4 is a file the person often cannot open. Proved
       the same way as the others: one real frame through the real encoder. */
    if (spec.muxerGlobal === 'Mp4Muxer' && await wasmH264Suits(width, height)) {
        var wasmLabel = profilesFor(spec, width, height)[0];
        if (await probeEncoder(wasmLabel, WASM_HINT, width, height, real(false))) {
            try { window.__ae && window.__ae('feature_use', { slug: window.__TOOL_SLUG || 'unknown', f: 'cve_wasm_h264', w: width, h: height }); } catch (_) {}
            return {
                codec: wasmLabel, hardware: false, provedByProbe: true, wasm: true,
                config: { codec: wasmLabel, width: width, height: height, bitrate: bitrate, framerate: fps }
            };
        }
    }
    if (spec.muxerGlobal === 'Mp4Muxer' && !h264Only && await probeEncoder(VP9_IN_MP4, 'no-preference', width, height, real(true))) {
        try { window.__ae && window.__ae('feature_use', { slug: window.__TOOL_SLUG || 'unknown', f: 'cve_vp9_rescue', w: width, h: height }); } catch (_) {}
        return {
            codec: VP9_IN_MP4, hardware: false, provedByProbe: true, vp9Rescue: true,
            config: { codec: VP9_IN_MP4, width: width, height: height, bitrate: bitrate, framerate: fps }
        };
    }
    // ── Last resort: attempt an UNVERIFIED config instead of surrendering ──
    // Ported from webcodecs-pipeline.js:436-460 (fixed there 2026-08-06,
    // commit 9aa491dbd8) — the same bug lived on here, and every canvas→video
    // tool rides THIS function: audio-to-video, slideshow-maker,
    // music-video-maker, storyboard, talking-avatar, reverse-video,
    // video-merge, remove-text, face-maker.
    //
    // `isConfigSupported` returning supported:false for EVERY profile × hint
    // is almost always a wedged GPU process, not a device that genuinely
    // cannot encode. Measured fleet-wide: all 160 enc_preflight skips in 14
    // days carried exactly this reason, and the same browsers encoded fine the
    // day before. The probe is advisory; `encoder.configure()` is the real
    // arbiter and throws within seconds when the encoder is truly broken —
    // at which point the existing fallback contract takes over.
    //
    // The asymmetry decides it: attempting costs seconds when broken, while
    // trusting a lying probe costs the hardware path entirely and drops the
    // user onto an FFmpeg-WASM grind.
    /* Every profile failed a REAL one-frame encode. The old comment here said
       an all-no answer «is almost always a wedged GPU process, not a device
       that genuinely cannot encode», and attempted anyway — correct while the
       only evidence was an advisory probe. It is no longer the only evidence:
       nothing above got a byte out of this machine for this container. Keep the
       attempt (an encoder that refuses 64×64 and accepts 1920×1080 is not
       impossible), but mark it so callers can tell a hopeful attempt from a
       proven one. */
    if (spec.videoProfiles.length) {
        return {
            codec: spec.videoProfiles[0],
            hardware: false,
            config: {
                codec: spec.videoProfiles[0], width: width, height: height,
                bitrate: bitrate, framerate: fps
            },
            unverified: true,
        };
    }
    return null;
}

/* crf (18 high / 23 medium / 28 low) → VBR bitrate. The frames audio-to-video
   produces are mostly flat synthetic content, which compresses to a fraction
   of the budget — so a generous bits-per-pixel still yields small files.
   ~0.16 bpp at crf 18 down to ~0.05 bpp at crf 28. */
/* The SOFTWARE H.264 rung's own bits — the one rule of window.__swEncoderBudget
   (timbrica-core.js; measured 2026-10-08: OpenH264 at the same bits is VMAF
   78–94 where the hardware encoder gives 96–98, and saturates near 96). Only
   H.264 (VP9/VP8 is a different software encoder, not measured), kept under
   the level the fixed codec string names, never below `bps`; 0 — no change.
   `softwareEncoderGain: false` opts out. No core — the old rate. */
function swRungBps(bps, w, h, fps, o, codec) {
    if ((o && o.softwareEncoderGain === false) || !/^avc1\./i.test(String(codec || ''))) return 0;
    var f = (typeof window !== 'undefined') && window.__swEncoderBudget;
    if (typeof f !== 'function') return 0;
    var b = f(bps, w, h, fps);
    var cap = (typeof f.levelCap === 'function') ? f.levelCap(codec) : 0;
    if (cap > 0 && b > cap) b = Math.max(bps, cap);
    return (b > bps) ? Math.round(b) : 0;
}

/* Is this rung of the hint ladder the SOFTWARE encoder? `prefer-software` always;
   `no-preference` when the codec pick found no hardware encoder for this config
   (vc.hardware false — Chromium then answers with its bundled software one).
   That is the FIRST rung of a desktop without a hardware H.264 encoder
   (`enc_verify_probe hw:0`: ~10 % of /enhance-video sessions), which the
   pipeline already covers — its desktop ladder goes hardware → software. */
/* The bits an encoder WRITES are bitrate ÷ the told rate per frame (OpenH264:
   ÷ min(told, 60)), and on Windows bitrate × told must not reach 2³² — the
   core's rule, window.__encoderRate (timbrica-core.js), rewrites the config.
   OpenH264's half of it is H.264-only: libvpx was not measured. */
function applyEncoderRate(cfg, softwareRung) {
    var f = (typeof window !== 'undefined') && window.__encoderRate;
    if (typeof f !== 'function' || !(cfg.framerate > 0)) return;
    if (softwareRung && !/^avc1\./i.test(cfg.codec || '')) return;
    var r = f(cfg.bitrate, cfg.framerate, cfg.framerate, softwareRung ? 'prefer-software' : (cfg.hardwareAcceleration || null));
    cfg.bitrate = r.bitrate; cfg.framerate = r.framerate;
}

function isSoftwareRung(hint, vc) {
    return hint === 'prefer-software' || (hint === 'no-preference' && !!vc && !vc.hardware);
}

function videoBitrateFor(w, h, fps, crf) {
    var c = parseInt(crf, 10); if (isNaN(c)) c = 23;
    var bpp = 0.16 - (Math.max(18, Math.min(28, c)) - 18) * 0.011; // 0.16 … 0.05
    return Math.round(Math.max(800000, Math.min(24000000, w * h * fps * bpp)));
}

/* ───────────────────────── audio codec probe ─────────────────────────
   AudioEncoder is present, but some browser/container/codec triples are
   unsupported (e.g. Opus at certain sample rates on some mobile builds —
   surfaced as "Failed to execute 'configure' on 'AudioEncoder':
   Unsupported codec type", 2 telemetry events / 14 d on prod). Probe up
   front so callers can fall back to FFmpeg cleanly instead of crashing
   mid-encode. Callers pass the clip's actual audioSampleRate /
   audioChannels — Opus@22050Hz mono can refuse where Opus@48000Hz stereo
   accepts on the same browser. */
async function probeAudioCodec(spec, sampleRate, channels) {
    if (typeof AudioEncoder === 'undefined' || !AudioEncoder.isConfigSupported) return false;
    try {
        var r = await AudioEncoder.isConfigSupported({
            codec: spec.audioCodec,
            sampleRate: sampleRate || 44100,
            numberOfChannels: channels || 2,
            bitrate: 192000
        });
        return !!(r && r.supported);
    } catch (_) { return false; }
}

/* ───────────────────────── support probe ───────────────────────── */
async function isSupported(opts) {
    opts = opts || {};
    var spec = CONTAINERS[opts.format || 'mp4'];
    if (!spec) return { ok: false, reason: 'bad-format' };
    /* `no-webcodecs` is about the VIDEO side only. iOS 16.4–18 ships
       VideoEncoder/VideoFrame without AudioEncoder/AudioData (Safari 26 added
       the audio half) — until 2026-08-26 that combination was refused here as
       "no WebCodecs at all", so every consumer's `no-audio-encoder` lane
       (remux the sound afterwards / split / mute) was unreachable exactly on
       the devices it was written for. Measured on /remove-text-from-video,
       30 days: 128 iOS sessions told "encoder unavailable" while 79 of the 81
       capability-sampled ones had VideoEncoder and lacked only the audio
       half. The audio probe below already answers `no-audio-encoder` when
       AudioEncoder is missing; encode() drops audio it cannot encode. */
    if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') {
        return { ok: false, reason: 'no-webcodecs' };
    }
    try { await ensureMuxer(spec); } catch (e) { return { ok: false, reason: (e && e.timedOut) ? 'muxer-timeout' : 'no-muxer' }; }
    if (!window[spec.muxerGlobal]) return { ok: false, reason: 'no-muxer' };
    var w = opts.width || 1920, h = opts.height || 1080, fps = opts.fps || 24;
    /* The caller's own bits (`bitrate`, or its crf), as encode() will use them —
       the verdict is about the encoder that will run (encoderConfig). */
    var bitrateAt = function (f) { return (opts.bitrate > 0) ? Math.round(opts.bitrate) : videoBitrateFor(w, h, f, opts.qualityCrf != null ? opts.qualityCrf : 23); };
    var vc = await pickVideoCodec(spec, w, h, bitrateAt(fps), fps, false, null, opts);
    var outFps = fps, fpsStep = null;
    /* ── The device's own H.264 at a lower rate, before VP9 (2026-10-30) ──
       A hardware H.264 encoder has a rate ceiling per size: Yandex 26.8 on an
       RTX 4060 makes 2560×1440 at 30 fps and not at 60 (measured), and Yandex
       ships no software one. At 60 fps such an export used to be VP9 inside the
       .mp4 — a file iPhone, WhatsApp and Windows' own player do not open — and
       took 7–12 min where the hardware encoder takes one (comment #1990: 4K60
       through VP9, 7 and 12 min). Half the rate keeps every other frame — the
       cadence of the source — and gives an H.264 file everyone opens. Only for
       a caller whose frames are drawn by TIME (`allowFpsStep`: drawFrame reads
       tSec), and only where the alternative is VP9 or an unproven attempt — x264
       at the full rate (vc.wasm) is H.264 already and keeps the rate. */
    if (opts.allowFpsStep && spec.muxerGlobal === 'Mp4Muxer' && vc && (vc.vp9Rescue || vc.unverified)) {
        for (var f = fps / 2; f >= 23; f = f / 2) {
            var lower = await nativeEncoderAt(spec, w, h, bitrateAt(f), f, opts);
            if (lower && /^avc1\./i.test(lower.codec || '')) {
                try { window.__ae && window.__ae('feature_use', { slug: window.__TOOL_SLUG || 'unknown', f: 'cve_fps_step', w: w, h: h, from: fps, to: f, instead: vc.vp9Rescue ? 'vp9' : 'unverified' }); } catch (_) {}
                vc = lower; outFps = f; fpsStep = { from: fps, to: f };
                break;
            }
        }
    }
    if (!vc) return { ok: false, reason: 'no-video-encoder' };
    var audioOk = await probeAudioCodec(spec, opts.audioSampleRate, opts.audioChannels);
    /* `proven` — a real frame came out of this machine for this container.
       `ok` deliberately stays true for an UNVERIFIED candidate: attempting is
       still right for a wedged GPU process, and taking that away would hand
       those users the realtime lane for nothing. What `proven:false` buys is
       the pre-flight: the caller can warn and offer the fast alternative BEFORE
       the wait instead of after it. */
    var proven = !vc.unverified;
    /* `lane` — which encoder the export will run on; `fps` — the rate it can
       run at (lower than asked only with allowFpsStep, see above). */
    var lane = vc.wasm ? 'wasm' : vc.vp9Rescue ? 'vp9' : vc.unverified ? 'unverified' : 'native';
    if (!audioOk) return { ok: false, reason: 'no-audio-encoder', proven: proven, lane: lane, fps: outFps, fpsStep: fpsStep };
    return { ok: true, hardware: vc.hardware, videoCodec: vc.codec, proven: proven, lane: lane, fps: outFps, fpsStep: fpsStep };
}

/* ───────────────────────── audio: AudioBuffer → encoder ─────────────────────
   Feeds decoded PCM to AudioEncoder in 1-second AudioData blocks
   (f32-planar). Capped at stereo — the WebCodecs path targets the
   overwhelmingly common mono/stereo case; multichannel sources go through
   the FFmpeg fallback in the blade.
   `tUsStart` is the timeline offset of this buffer's first sample — 0 for the
   whole-timeline audioBuffer path; a running offset for chunked feeding.
   Returns the end-of-buffer timestamp so chunk feeders can chain. */
function encodeAudio(audioEncoder, audioBuffer, channels, throwIfAborted, tUsStart) {
    var sr = audioBuffer.sampleRate;
    var total = audioBuffer.length;
    var block = sr;                       // 1 s per AudioData
    var ch0 = audioBuffer.getChannelData(0);
    var ch1 = channels > 1 ? audioBuffer.getChannelData(1) : ch0;
    var tUs = tUsStart || 0;
    for (var off = 0; off < total; off += block) {
        throwIfAborted();
        var n = Math.min(block, total - off);
        var data = new Float32Array(n * channels); // f32-planar: [ch0…, ch1…]
        data.set(ch0.subarray(off, off + n), 0);
        if (channels > 1) data.set(ch1.subarray(off, off + n), n);
        var ad = new AudioData({
            format: 'f32-planar', sampleRate: sr,
            numberOfFrames: n, numberOfChannels: channels,
            timestamp: Math.round(tUs), data: data
        });
        audioEncoder.encode(ad);
        ad.close();
        tUs += (n / sr) * 1e6;
    }
    return tUs;
}

/* ── liveness heartbeat ──────────────────────────────────────────────────────
   Every long lane on this site publishes `stall-tick` so __withStallGuard can
   tell "slow" from "wedged"; the FFmpeg engine got that on 2026-08-31. This
   lane — 21 modules and 7 blades — never did, and the difference is not
   cosmetic: measured 2026-09-03 by fault injection on /remove-text-from-video,
   a producer that under-delivers or an encoder that stops draining leaves the
   page waiting FOREVER with a frozen bar, no toast, no error and no telemetry
   (75 s and counting on both faults). Prod agrees: 872 of 2497 export sessions
   in 30 days ended with no completion, no cancel and no error.

   One tick meaning covers both sides of the rendezvous. The encoder blocks on
   drawFrame when the producer stalls, and the producer blocks on back-pressure
   when the encoder stalls — so "a frame went through" is alive for the whole
   pipeline, and silence is unambiguous. */
function beat() { try { window.dispatchEvent(new CustomEvent('stall-tick')); } catch (_) {} }

/* The one silence threshold for this lane, exported so there is a single number
   rather than a copy per caller — and so a test can prove the guard actually
   trips without sitting through three minutes of real silence. Three minutes is
   the floor the FFmpeg engine settled on for the same reason: a legitimate frame
   on a weak device can take many seconds, so the guard is `adaptive` on top of
   this (max(floor, 3 x EMA of the gap between ticks)) and only ever judges
   SILENCE — never how long the export has been running. */
var STALL_FLOOR_MS = 180000;
/* The largest estimated output encode() still takes into MEMORY when there is
   no disk lane. One number for encode()'s own refusal and for a caller that
   offers «build it in memory» as a way out.
   With the block target (window.__memoryMuxTarget) it is what the device's RAM
   allows — window.__memoryMuxCeiling(): 1536 / 640 / 256 MiB. Without it (an
   older core next to this file) it is the one-buffer wall, MEASURED 14.10.2026
   on Chrome 155: 1000 MB lands, 1100 MB dies with «Array buffer allocation
   failed» at finalize. Until then this was a constant 1200 MB — above the
   wall: a file of 1.0–1.2 GB with no disk lane encoded to the end and died. */
var ONE_BUFFER_WALL = 1000 * 1048576;
function memoryCeiling() {
    try {
        if (typeof window.__memoryMuxTarget === 'function' && typeof window.__memoryMuxCeiling === 'function') {
            var c = +window.__memoryMuxCeiling();
            if (c > 0) return c;
        }
    } catch (_) {}
    return ONE_BUFFER_WALL;
}

/* ───────────────────────── main encode ───────────────────────── */
/* Guarded entry point. The wrap lives HERE, not in the 21 callers, for the same
   reason the FFmpeg guard moved into FFmpegProcessor: a blade can forget it,
   and the ones that forgot are exactly the ones nobody was watching. `depth`
   keeps a caller that already wrapped its own larger span (recon-pipeline wraps
   analyze+stream+encode) from arming a second, competing watchdog. */
var guardDepth = 0;
/* ── теги источника → наш контейнер ─────────────────────────────────────────
 * mp4-muxer не знает слова `ilst`, поэтому всё, что собрано здесь, выходит БЕЗ
 * названия, исполнителя и альбома — а тот же тул, упавший на FFmpeg-лейн,
 * отдаёт их на месте (`-map_metadata 0` — умолчание ffmpeg). Одна кнопка, два
 * ответа, разница — браузер (обращение #8219, /video-watermark, 20.09.2026).
 *
 * Источник вызывающий может назвать сам (`o.sourceFile`), но у трёх десятков
 * потребителей этого модуля на руках КАДРЫ, а не файл. Обязать каждого —
 * значит превратить правило в список, который следующий тул не пополнит.
 * Поэтому при молчании вызывающего спрашиваем зону загрузки — она владеет
 * вопросом «какой файл на странице» и отвечает только когда он ОДИН и он
 * mp4 (см. __mp4Tags.pageSource). `carryTags:false` — явный отказ.
 */
async function ensureMp4Tags() {
    if (window.__mp4Tags) return window.__mp4Tags;
    if (!ensureMp4Tags._p) {
        var src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/mp4-tags.js') : '/js/mp4-tags.js';
        ensureMp4Tags._p = loadOnce('__mp4TagsLoading', [src, retryUrl('/js/mp4-tags.js')],
            function () { return !!window.__mp4Tags; }).catch(function () { });
    }
    try { await ensureMp4Tags._p; } catch (_) { }
    return window.__mp4Tags || null;
}

async function carryTagsInto(blob, o) {
    if (!blob || !blob.size || o.carryTags === false) return blob;
    try {
        var T = await ensureMp4Tags();
        if (!T) return blob;
        var src = o.sourceFile || T.pageSource();
        if (!src) return blob;
        var next = await T.carry(src, blob, { slug: window.__TOOL_SLUG || '' });
        if (next === blob) return blob;
        /* Вердикт о размере выхода навешен на объект — он про те же байты. */
        try { if (blob.__cveVerdict) next.__cveVerdict = blob.__cveVerdict; } catch (_) { }
        return next;
    } catch (_) { return blob; }
}

/* ── an encode must have an END ─────────────────────────────────────────────
 * `durationSec` sets the frame count, and Infinity made it infinite: the loop
 * ran for hours, progress (i / Infinity) sat at 0 — which callers show as their
 * floor, «2 %» on /slideshow-maker — and nothing ever errored (support chat 655,
 * 28–29.09.2026: a recorder WebM named «.wav» whose `<audio>.duration` is
 * Infinity in Chromium became the length of the show). A caller that does not
 * know how long its output is has a bug upstream; this makes it say so at
 * once, typed, instead of encoding forever. */
function durationUnknownError(d) {
    var e = new Error('duration-unknown');
    e.code = 'DURATION_UNKNOWN';
    e.durationSec = d;
    return e;
}
function assertFiniteDuration(o) {
    var d = o && o.durationSec;
    if (typeof d === 'number' && isFinite(d) && d > 0) return;
    try {
        if (window.__ae) window.__ae('feature_use', {
            slug: window.__TOOL_SLUG || (window.__currentSlug || ''), f: 'cve_duration_refused', d: String(d)
        });
    } catch (_) {}
    throw durationUnknownError(d);
}

async function encode(o) {
    o = o || {};
    assertFiniteDuration(o);
    var out;
    try {
        out = await encodeGuarded(o);
    } catch (e) {
        if (!h264FellThrough(e, o)) throw e;
        /* Every H.264 this export had died — the browser's own rungs and, where
           it suits the size, our x264. Before the x264 rung existed this export
           was VP9 in the .mp4 from the start; that is what it gets now, from
           frame 0 (a codec cannot change inside one track). Until 30.10 only an
           x264 death came here: a ladder WITHOUT the x264 rung (above 1080p on
           a page that is not cross-origin isolated) ended in the error — prod
           29.10 18:29, /video-editor, Yandex, 1440p: «Failed to create OpenH264
           encoder», no file, after the rung had shipped.
           A caller that said «H.264 or nothing» (h264Only — the slideshow has
           its own honest transcode) and a caller that cannot replay its frames
           get the error instead. */
        try {
            var hs = e.encoderHints || [];
            ae('cve_h264_fell_through', { slug: window.__TOOL_SLUG || 'unknown', why: String((e && (e.name + ': ' + e.message)) || e).slice(0, 100),
                hints: hs.join('>'), last: hs[hs.length - 1] || '', wasm: hs.indexOf(WASM_HINT) >= 0 ? 1 : 0, at: e.encoderAtFrame | 0 });
        } catch (_) {}
        out = await encodeGuarded(Object.assign({}, o, { _codecRung: 'vp9' }));
    }
    var res = await carryTagsInto(out, o);
    // The tagged copy is built from slices of the same disk file: releasing it releases that file.
    if (res !== out && resultFiles && resultFiles.has(out)) { try { resultFiles.set(res, resultFiles.get(out)); } catch (_) {} }
    return res;
}

/* ── the result's disk file lives as long as the page wants it ─────────────
 * A disk-lane result IS its OPFS file. Until 18.10.2026 a timer removed that
 * file 30 min after the render, on a living page — the clip on screen then
 * answered «Скачать» with NotFoundError (/audio-to-video, RU, 30 d to 18.10:
 * 26 presses in 14 sessions, 10 of them 32–274 min after the render; the door's
 * `blob_unreadable` 33–1240 min after a render on /video-editor, /slideshow-maker,
 * /reverse-video, /remove-text-from-video, /music-video-maker — 15 sessions,
 * the same exporter). A result is not a leftover at
 * any age — the same lesson as OutputSink's hourly sweep (opfs-owner.md, 14.10).
 * The janitor (__opfsOwner) removes the files of pages that are gone; a tool
 * that is done with a result — a new one replaced it — gives the disk back with
 * CanvasVideoExport.release(blob). */
var resultFiles = typeof WeakMap === 'function' ? new WeakMap() : null;
function release(blob) {
    var e = (resultFiles && blob) ? resultFiles.get(blob) : null;
    if (!e) return false;
    try { resultFiles.delete(blob); } catch (_) {}
    try { e.root.removeEntry(e.name).catch(function () {}); } catch (_) {}
    return true;
}

/* The video cascade ran out of H.264 rungs (encoderExhausted, set only by the
   frame loop — an audio encoder's death is not this) on an H.264 export. */
function h264FellThrough(e, o) {
    return !!(e && e.encoderExhausted && /^avc1\./i.test(String(e.encoderCodec || ''))
        && (o.format || 'mp4') === 'mp4' && !o.h264Only && o.canReplayFrames !== false
        && !o._codecRung && !(o.signal && o.signal.aborted));
}

async function encodeGuarded(o) {
    if (guardDepth > 0 || typeof window.__withStallGuard !== 'function' || o.noStallGuard) return encodeInner(o);
    guardDepth++;
    var kill = { stalled: false };
    try {
        return await window.__withStallGuard(function () { return encodeInner(o, kill); }, {
            stage: 'encode',
            // Silence, never duration: a legitimate frame on a weak phone can
            // take many seconds (MI-GAN reconstruction), so the floor is the
            // same 3 min the FFmpeg engine settled on, and the soft toast at
            // half that tells the user it is alive before anything is killed.
            adaptive: true, floorMs: api.stallFloorMs, hardMs: api.stallFloorMs,
            // Half the threshold, explicitly. The guard's own default is
            // max(30 s, hard/2), which silently exceeds any hard floor under
            // 60 s — so at a lowered threshold the "still working, you can
            // cancel" warning would never appear and the first thing the user
            // saw would be the failure. Proportional keeps the two in order at
            // every scale, which is also what makes the guarantee testable.
            softMs: Math.round(api.stallFloorMs * 0.5),
            meta: { slug: window.__TOOL_SLUG || (window.__currentSlug || ''), lane: 'canvas-video-export' },
            // Guard what we guard — see telemetry-tier0.js. Cancelling the
            // caller's AbortController unwinds the whole pipeline (producer
            // included); the default fp.cancel() would have shot the wrong engine.
            cancel: function () {
                kill.stalled = true;                       // stop the orphaned loop
                try { o.onStallAbort && o.onStallAbort(); } catch (_) {}
            }
        });
    } finally { guardDepth--; }
}

async function encodeInner(o, kill) {
    var signal = o.signal;
    function throwIfAborted() {
        /* The watchdog RACES — it settles the caller without waiting for us — so
           this flag is what actually frees the encoder, the muxer and the OPFS
           temp file. Without it a tripped guard would leave a full pipeline
           running invisibly behind the error the user just read. */
        if (kill && kill.stalled) throw new DOMException('Aborted', 'AbortError');
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        /* A streamed output (moov at the end) writes while it encodes: once the
           disk has refused a write, every further frame is work for a file that
           will not close — stop now, not at finalize. (`lane` is hoisted.) */
        if (lane && lane.stats && lane.stats.failed) throw diskWriteError(lane.stats.failed, lane.picked);
    }
    throwIfAborted();

    var spec = CONTAINERS[o.format || 'mp4'];
    if (!spec) throw new Error('unsupported-format:' + o.format);
    await ensureMuxer(spec);
    var MuxerNs = window[spec.muxerGlobal];
    if (!MuxerNs) throw new Error('muxer-not-loaded:' + spec.muxerGlobal);

    var w = o.width, h = o.height, fps = o.fps || 24;
    /* A source whose frame rate changes inside the file (a phone in dim light,
       a screen recording) is re-encoded at ITS times: frame i at frameTimes[i],
       as long as until the next. At one rate there is no right answer for it —
       the average drops frames where it ran faster, a denser grid repeats them
       (#9567, 2026-10-16: the average lost 82 of 581 frames, the next integer
       rate repeated 138). `fps` stays the nominal rate the codec is configured
       with; keyframes and the audio pump follow the clock, not the index. */
    var ft = (o.frameTimes && o.frameTimes.length) ? o.frameTimes : null;
    var totalFrames = ft ? ft.length : Math.max(1, Math.round(o.durationSec * fps));
    var _ftT0 = ft ? ft[0] : 0;
    function frameTimeSec(i) { return ft ? ft[i] - _ftT0 : i / fps; }
    function frameLenUs(i) {
        if (!ft) return Math.round(1e6 / fps);
        var end = i + 1 < ft.length ? ft[i + 1] : Math.max(ft[i] + 1 / fps, (o.durationSec || 0) + _ftT0);
        return Math.max(1, Math.round((end - ft[i]) * 1e6));
    }
    /* An explicit `bitrate` wins over the crf→bpp mapping. The mapping was tuned
       for audio-to-video's flat synthetic frames (0.16 bpp at "crf 18"); on real
       footage it sits ~5 dB below x264 at the same crf, and a clip that is being
       NORMALIZED next to byte-copied camera clips must not drop visibly below
       its own source — the caller knows the source density, this function does
       not. openStream() already honoured o.bitrate; encode() now does too. */
    var vbr = (o.bitrate > 0) ? Math.round(o.bitrate) : videoBitrateFor(w, h, fps, o.qualityCrf);

    /* ── free-tier brand mark ────────────────────────────────────────────────
     * Resolved ONCE per export, not per frame: __wmShould reads the tier
     * snapshot, and re-reading it 1 800 times would let a bootstrap that lands
     * mid-encode produce a half-marked file.
     *
     * `selfWatermark: true` opts a caller out — /audio-to-video stamps inside
     * its own renderFrame so that preview and export are identical, and it also
     * feeds an FFmpeg path this module never sees; stamping again here would
     * double-draw the same corner.
     *
     * Everything is fail-closed to NOT marking: no primitive, no scope, unknown
     * tier ⇒ clean export. A missing mark costs a nudge; a wrong one brands a
     * paying customer's film. */
    var wmOn = false;
    try {
        wmOn = !o.selfWatermark
            && !!window.__wmShould
            && !!window.__wmDraw
            && window.__wmShould(window.__WM_SCOPE);
    } catch (_) { wmOn = false; }
    function stampWatermark(ctx2d, fw, fh) {
        if (!wmOn) return;
        try { window.__wmDraw(ctx2d, fw, fh, window.__WM_TEXT); } catch (_) {}
    }
    /* One event per export, not per frame. Reports whether THIS lane marked, so
     * the lane gap is measurable rather than assumed: several tools in scope
     * also own a MediaRecorder or FFmpeg lane this module never sees, and the
     * only way to know how much of the traffic they carry is to count the lane
     * that DID stamp against the tool's total completions. */
    try {
        window.__ae && window.__ae('feature_use', {
            slug: window.__TOOL_SLUG || 'unknown',
            f: 'wm_lane',
            lane: 'canvas',
            marked: wmOn ? 1 : 0,
            scope: window.__WM_SCOPE ? 1 : 0
        });
    } catch (_) {}

    /* What this export ACTUALLY delivers, in the plainest terms the page can
     * state: a height and an fps. Reported raw — whether it sits above the free
     * ceiling is decided SERVER-side (RenderedPaidService), where the ceiling
     * already lives in config/tiers.php and can be corrected without shipping
     * new client code.
     *
     * It had to be added: quality-tier.js only ever emits for FREE users («a Pro
     * user has nothing locked»), so a paying buyer's above-ceiling export left
     * no trace at all — which is how refund request #19 (1440p, free ceiling
     * 720) read as "never used the paid product". */
    try {
        window.__ae && window.__ae('feature_use', {
            slug: window.__TOOL_SLUG || 'unknown',
            f: 'quality_delivered',
            height: h, fps: fps, format: o.format || 'mp4'
        });
    } catch (_) {}

    var vc = await pickVideoCodec(spec, w, h, vbr, fps, !!o.h264Only, o._codecRung, o);
    /* ── encoder hint cascade (2026-08-27) ────────────────────────────────
       isConfigSupported is a capability check, not an init: on prod 447
       «OOM»-prompts / 14 d in /slideshow-maker were OperationError from the
       HW encoder dying at the FIRST frames (398 of them ≤3 s after export
       start, 92 % Yandex Browser, devMem 4/8/16 alike) — and stepping the
       resolution down never helped (480p had the most prompts). Mirror of
       webcodecs-pipeline.js (measured there: rebuild lifts completion
       46.5 % → 78 %): when the encoder dies, rebuild with the next hint and
       resume at the first frame it never emitted. Android: software first
       (its HW H.264 stalls, see webcodecs-pipeline.js ENCODER_HINTS). */
    var _isAndroid = /Android/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
    var hintOrder = (vc && vc.wasm) ? [WASM_HINT] : _isAndroid
        ? ['prefer-software', 'no-preference']
        : (vc && vc.hardware ? ['prefer-hardware', 'no-preference', 'prefer-software'] : ['no-preference', 'prefer-software']);
    /* The rung below a native H.264 that dies mid-export (2026-10-29). The
       ladder above ends at `prefer-software`, which on Yandex Browser does not
       exist: prod, slideshow, October — 34 sessions where every native rung
       died (Android HW «Encoding error», desktop HW dead and «Failed to create
       OpenH264»), 14 of them never got a file. x264 continues from the first
       frame the dead encoder never emitted; its parameter sets differ, and
       __muxVideoTrack carries them in-band on its keyframes. Above 1080p only
       where it can run threaded (WasmH264Encoder.suits). */
    if (vc && !vc.wasm && /^avc1\./i.test(vc.codec || '') && spec.muxerGlobal === 'Mp4Muxer' && await wasmH264Suits(w, h)) hintOrder.push(WASM_HINT);
    var hintIdx = 0;
    var chunksOut = 0;            // frames the CURRENT stream produced (1 chunk/frame, no B-frames)
    /* Bytes the ENCODER actually emitted, summed over every chunk handed to
       the muxer. This is the only number that can contradict the delivered
       file: a container that silently loses writes, and an encoder handed
       blank frames, are indistinguishable from the file size alone — and
       both ship as `export_complete`. See verifyOutput() below. */
    var videoBytesOut = 0, audioBytesOut = 0;
    var rebuilds = 0;
    if (!vc) throw new Error('no-encoder');
    /* The software rung asks for its own bits (swRungBps): at `vbr` OpenH264
       starves where the hardware encoder does not — on Android that is the
       FIRST rung, i.e. every export. 0 — the rung uses `vbr`. */
    var swVbr = swRungBps(vbr, w, h, fps, o, vc.codec);

    var ab = o.audioBuffer;
    /* Chunked audio — `audioChunks: { sampleRate, channels, next: async () => AudioBuffer|null }`.
       Alternative to audioBuffer for LONG timelines: the whole-timeline AudioBuffer
       is O(duration) heap (a 3-hour merge ≈ 4 GB of PCM — the other half of the
       fokus33 failure). A provider yields the timeline as sequential AudioBuffer
       chunks; each is fed to the AudioEncoder and released, so heap stays O(chunk).
       Chunks are pulled INTERLEAVED with the video loop (audio kept just behind
       video time) so the muxer writes A/V interleaved — matters for the
       fastStart:false streaming layout. */
    var chunkSrc = (!ab && o.audioChunks && typeof o.audioChunks.next === 'function') ? o.audioChunks : null;
    var hasAudio = !!(ab && ab.length > 0 && ab.numberOfChannels > 0) || !!chunkSrc;
    /* Audio handed in on a browser without the audio half of WebCodecs (iOS
       16.4–18): isSupported() said `no-audio-encoder` and the caller should
       have muted or planned a remux — if it still passed audio, dropping it is
       the honest outcome, a crash on `new AudioEncoder` is not. */
    if (hasAudio && (typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined')) {
        hasAudio = false; ab = null; chunkSrc = null;
        try { window.__ae && window.__ae('feature_use', { f: 'cve_audio_dropped_no_encoder' }); } catch (_) {}
    }
    var audioSR = ab ? ab.sampleRate : (chunkSrc ? (chunkSrc.sampleRate || 48000) : 44100);
    var audioCh = ab ? Math.min(2, ab.numberOfChannels) : (chunkSrc ? Math.min(2, chunkSrc.channels || 2) : 2);
    var audioBitrate = o.audioBitrate || 192000;

    /* Output target — OPFS (disk-backed) by default, ArrayBuffer fallback.
     *
     * Why disk-backed: holding the entire encoded MP4 in a JS ArrayBuffer
     * caps us at the Chromium ~2 GB heap allocation limit AND piles peak
     * memory on top of the renderer at download time, which is what
     * OOM-kills budget Android tabs at the "Save" step (slideshow-maker
     * feedback `f71525fe`, 2026-05-23: TECNO Android, "выключается
     * телефон и все сбрасывается" — telemetry showed 1-in-5 completed
     * exports > 200 MB, 1-in-2 > 100 MB). The same OOM defect class hit
     * screen-recorder earlier (Andrey, 99-min recording lost,
     * f01dbfcb 2026-05-08); that tool moved to OPFS and the issue went
     * silent. Re-use the same primitive here.
     *
     * OPFS = Origin Private File System: a sandboxed disk filesystem
     * available without ANY user picker or permission prompt. Chrome 102+
     * / Firefox 111+ / Safari 15.2+ — ≥95 % of real visitors. The encoder
     * writes chunks straight to disk via the muxer's stream-target; the
     * JS heap only ever holds the in-flight chunk + the encoder's small
     * internal queue. fastStart='in-memory' (default mp4-muxer setting)
     * works because FileSystemWritableFileStream supports .seek() — the
     * finalize step seeks back to offset 0 to write the moov atom.
     *
     * After encode we return the `File` handle (which IS a Blob, so the
     * caller's download path works unchanged). A 5-minute timer cleans
     * up the OPFS temp file — by then the user has either saved it or
     * navigated away.
     *
     * The disk lane RESERVES its room before frame 0 (__opfsMuxLane in
     * timbrica-core.js). Until 2026-09-25 it only opened a file: a browser
     * whose grant was a few MB — a nearly full system disk, invisible to
     * estimate() on Chromium — let the export encode for minutes and refused
     * every byte at finalize, and the caller called it «not enough RAM»
     * (support chat 647: six exports, 3–11 min each, while the same deck
     * landed in memory whenever the open itself happened to be refused). */
    var estOutBytes = ((((isSoftwareRung(hintOrder[0], vc) && swVbr) ? swVbr : vbr) + (hasAudio ? audioBitrate : 0)) / 8) * (o.durationSec || 0);
    var target;
    var lane = null, diskRefused = false;
    /* `outputHandle` — a file the PERSON picked (showSaveFilePicker): the muxer
       writes straight into it, on whatever disk it is, and the browser's own
       storage is not asked for anything (report 12.10.2026, /video-editor: five
       builds refused by a full system disk, and nowhere else to put the file —
       __pickedMuxLane in timbrica-core.js). A disk that refuses the room here is
       an answer for the person, never a silent fall into memory: they chose
       where the file goes. */
    var pickedLane = false;
    if (o.outputHandle && typeof window.__pickedMuxLane === 'function' && typeof MuxerNs.StreamTarget === 'function') {
        var _pickedOpen = window.__pickedMuxLane(MuxerNs, o.outputHandle, {
            lane: 'cve', reserveBytes: Math.round(estOutBytes * 1.15) + 8 * 1048576
        });
        try {
            lane = await withTimeout(_pickedOpen, SETUP_TIMEOUTS.picked, 'picked');
        } catch (e) {
            if (e && e.timedOut) { try { _pickedOpen.then(function (x) { if (x) x.abort(); }, function () {}); } catch (_) {} }
            var cantOpen = new Error('picked-open-failed:' + ((e && e.message) || e));
            cantOpen.picked = true; cantOpen.cause = e;
            throw cantOpen;
        }
        if (!lane) {
            var noRoom = new Error('picked-disk-refused:' + Math.round(estOutBytes / 1048576) + 'MB');
            noRoom.diskFull = true; noRoom.picked = true;
            throw noRoom;
        }
        target = lane.target; pickedLane = true;
    } else
    if (o.noDiskTarget !== true &&
        typeof navigator.storage !== 'undefined' &&
        typeof navigator.storage.getDirectory === 'function' &&
        typeof MuxerNs.StreamTarget === 'function' && typeof window.__opfsMuxLane === 'function') {
        var _opfsOpen = null;
        try {
            /* The storage calls under ONE deadline: a locked storage backend
               answers none of them, and until 2026-09-17 that hang sat before
               frame 0 with nothing to trip the stall guard.
               The reservation carries 15 % + 8 MB over the bitrate estimate
               (measured: slideshow exports land at 0.87–0.88 of it); a file
               that still outgrows it just grows, asking the grant again. */
            _opfsOpen = window.__opfsMuxLane(MuxerNs, {
                prefix: '_cve_', ext: o.format === 'webm' ? 'webm' : 'mp4', lane: 'cve',
                reserveBytes: Math.round(estOutBytes * 1.15) + 8 * 1048576
            });
            lane = await withTimeout(_opfsOpen, SETUP_TIMEOUTS.opfs, 'opfs');
            /* The lane hands the muxer the shared exact-size target, not the
               muxer's own FileSystemWritableFileStreamTarget: WebKit writes the
               whole 16 MiB chunk buffer behind each subarray view it is handed
               (report #7211's 64 MiB slideshow) — see timbrica-core.js. */
            if (lane) target = lane.target;
            else diskRefused = true;
        } catch (e) {
            // OPFS unavailable (Safari private mode, etc.) — fall
            // through to the in-memory path. Same risk as before this fix.
            lane = null;
            if (e && e.timedOut) {
                // A late answer must not leak an open handle behind the export.
                try { _opfsOpen.then(function (x) { if (x) x.abort(); }, function () {}); } catch (_) {}
                try { window.__ae && window.__ae('feature_use', { slug: window.__TOOL_SLUG || 'unknown', f: 'cve_opfs_timeout', ms: SETUP_TIMEOUTS.opfs }); } catch (_) {}
            }
        }
    }
    /* A disk lane that could NOT hold its room is not trusted when memory can
       take the file. Measured 14.10.2026 (Chrome 155, Yandex 26.8): a write
       inside a held reservation is never refused — so every `opfs-write-failed`
       of the field (74 sessions in 30 days, the report of 12.10 among them)
       came from a lane without its room: an off-the-record profile, where the
       reservation is skipped on purpose and the «disk» is RAM under a small
       grant (24 of the 74), or a reservation that failed for another reason.
       Such a lane fails minutes into the encode; memory does not depend on the
       grant at all. Above the memory ceiling there is nothing better — the lane
       is used as before, and its refusal is the caller's panel.
       The same for a lane on a SHORT disk (lane.diskShort): there the browser
       itself deletes the site's storage under the open file — quota eviction,
       reproduced 14.10.2026; 21 field sessions in 30 days ended in
       «NotFoundError … could not be found» — and memory cannot be evicted. */
    var memCeiling = memoryCeiling();
    var laneDoubt = (lane && !pickedLane) ? ((!lane.reserved && lane.unreservedWhy) ? String(lane.unreservedWhy) : (lane.diskShort ? 'short' : '')) : '';
    if (laneDoubt && estOutBytes > 0 && estOutBytes <= memCeiling) {
        try { window.__ae && window.__ae('feature_use', { slug: window.__TOOL_SLUG || 'unknown', f: 'cve_unreserved_to_memory', why: laneDoubt.slice(0, 40), mb: Math.round(estOutBytes / 1048576) }); } catch (_) {}
        try { lane.abort(); } catch (_) {}
        lane = null; target = null;
    }
    var opfsWritable = lane ? lane.writable : null;
    /* Memory for a BIG file is blocks, not one buffer (__memoryMuxTarget in
       timbrica-core.js): the muxer's own ArrayBufferTarget doubles — past 1 GiB
       it asks for 2 GiB and dies with RangeError at finalize, after the whole
       encode — and with fastStart 'in-memory' it peaks at ~4x the file
       (measured: 1000 MB → 2.9–4.0 GB; blocks → 1.5 GB, and 1900 MB whole). */
    var memTarget = null;
    if (!target && estOutBytes > 300 * 1048576 && estOutBytes <= memCeiling &&
        typeof window.__memoryMuxTarget === 'function' && typeof MuxerNs.StreamTarget === 'function') {
        memTarget = window.__memoryMuxTarget(MuxerNs);
        target = memTarget.target;
    }
    if (!target) target = new MuxerNs.ArrayBufferTarget();

    var muxerCfg = {
        target: target,
        // mp4-muxer's `frameRate` IS the track timescale — fixed, not the clip's
        // rate. Rationale + measurements: MUX_VIDEO_TIMEBASE in webcodecs-pipeline.js.
        // Kept in step by Mp4MuxerTimebaseTest.
        video: { codec: muxVideoCodecFor(spec, vc), width: w, height: h, frameRate: 120000 },
        audio: hasAudio ? { codec: spec.muxerAudioCodec, numberOfChannels: audioCh, sampleRate: audioSR } : undefined,
        firstTimestampBehavior: 'offset'
    };
    Object.assign(muxerCfg, spec.muxerExtra);   // mp4 → fastStart:'in-memory'
    /* fastStart SIZE POLICY. mp4-muxer's 'in-memory' fastStart buffers the ENTIRE
       output in one growing ArrayBuffer until finalize — and Chromium caps a single
       ArrayBuffer at ~2.1 GB, so any output past that dies with
       "RangeError: Array buffer allocation failed" no matter how much RAM the
       machine has (video-merge feedback fokus33, 2026-07-02: 9×8-min clips ≈ 5.7 GB
       estimated output; probe-verified: new ArrayBuffer(2200MB) throws in Chromium).
       When the estimated output exceeds a conservative slice of that wall AND we
       have a disk-backed stream target, switch to fastStart:false — media data
       streams to OPFS as it's encoded (O(1) heap) and the moov atom is written at
       the END. moov-at-end plays fine in local players (it only forgoes
       progressive-download start). Small outputs keep 'in-memory' (moov at head,
       zero behavior change for the common case). No OPFS + huge estimate → throw
       a typed error BEFORE encoding so callers can message honestly instead of
       dying 20 minutes in. */
    if (spec.muxerExtra && spec.muxerExtra.fastStart === 'in-memory' && estOutBytes > 300 * 1048576) {
        if (opfsWritable || memTarget) {
            // A stream layout (moov at the end): the disk lane, and the blocks in memory.
            muxerCfg.fastStart = false;
        } else if (estOutBytes > memCeiling) {
            var tooBig = new Error('output-too-large-no-opfs:' + Math.round(estOutBytes / 1048576) + 'MB');
            // The disk exists but refused the room — a different sentence for the user.
            if (diskRefused) tooBig.diskFull = true;
            throw tooBig;
        }
        // Under the ceiling without the block target (an older core): one buffer, as before.
    }
    var muxer = new MuxerNs.Muxer(muxerCfg);
    // Parameter sets that change mid-stream travel in-band (report #8247) — see __muxVideoTrack.
    var muxVideo = window.__muxVideoTrack ? window.__muxVideoTrack(muxer) : function (c, m, t, o) { return muxer.addVideoChunk(c, m, t, o); };

    var encErr = null;
    var framesPulled = 0;              // highest drawFrame index served + 1 (see rebuildVideoEncoder)
    var DEFAULT_COLOR_SPACE = { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };
    var firstVideoChunk = true;
    /* B-frame-safe muxing — running decode-order DTS + compositionTimeOffset.
       latencyMode:'realtime' (below) only HINTS no B-frames; HW encoders and
       Firefox (High profile) emit them anyway, so the chunk .timestamp
       (presentation/CTS) is non-monotonic and mp4-muxer rejects the derived
       DTS with "Timestamps must be monotonically increasing". Chunks ALWAYS
       arrive in decode order, so DTS = running sum of durations (strictly
       monotonic); compositionTimeOffset = CTS − DTS makes the muxer write a
       ctts box. Ported from webcodecs-pipeline.js addVideoChunk, which fixed the
       same "DTS went from X to Y" cluster (120 enhance-video sessions/14d).
       Decode time (04.11.2026): «for a no-B-frame stream the offset is 0» was
       said here and was false — the running sum of ROUNDED chunk durations
       (41 667 µs at 24 fps) ran ahead of the frames' own times, every offset
       went −1…−10 ticks into an unsigned ctts, and our byte-range cut read them
       as hours (/reverse-video → /video-trim kept 14 frames of 216). The decode
       time is now the clock's: the k-th smallest frame time fed
       (window.__encoderClock … dts). The sum stays only for a core without it. */
    var _videoDtsAccumUs = 0;
    var _frameDurFallbackUs = 1e6 / fps;
    /* WHICH frame an output chunk is: Firefox's H.264 encoder with B-frames stamps
       a chunk with the END of its frame — window.__encoderClock (timbrica-core.js).
       At one frame rate that is one constant shift (a video-only file started 48 ms
       late), at `frameTimes` every frame at its neighbour's time (#9567, the reporter's
       browser: 92 of 581 moments showed another picture). */
    var _clock = window.__encoderClock ? window.__encoderClock({ onEnd: function () {
        ae('cve_chunk_end_stamped', { slug: window.__TOOL_SLUG || 'unknown', codec: String(vc && vc.codec || '').slice(0, 20), vfr: ft ? 1 : 0 });
    } }) : null;
    function _chunkTimestamp(chunk) { return _clock ? _clock.ts(chunk) : chunk.timestamp; }

    var videoEncoder = null;
    /* ── An encoder's death belongs to that encoder (2026-10-30) ──
       A codec reports its death twice: `error`, posted, with the reason, and —
       when encode() is called after it has closed and before that post lands —
       a synchronous InvalidStateError that only says «closed». The loop below
       catches the second and rebuilds at once, so (1) the cascade reported
       «Cannot call encode on a closed codec» instead of the reason (prod 29.10,
       Android: the x264 rung died after 5 frames and nothing said why; the
       stand reproduces it with WasmH264Encoder, whose _fail closes at once and
       posts the error a task later), and (2) the dead encoder's posted error,
       arriving after the rebuild, was charged to the NEW encoder and burnt its
       rung too. Now an error counts only for the encoder that is current
       (`encGen`), a synchronous «closed» is marked weak, and the rebuild waits
       one task for the real reason before reading it. */
    var encGen = 0;
    function syncClosed(t) {
        var e = t instanceof Error ? t : new Error(String(t && t.message || t));
        try { e.__syncClosed = true; } catch (_) {}
        return e;
    }
    function makeVideoEncoder(hint) {
      var gen = ++encGen;
      var enc = newVideoEncoder({
        output: function (chunk, meta) {
            var safe = meta || {};
            chunksOut++;
            videoBytesOut += (chunk.byteLength || 0);
            // muxers want a colorSpace on the first chunk's decoderConfig
            // (defensive patch mirrored from webcodecs-pipeline.js).
            if (firstVideoChunk) {
                firstVideoChunk = false;
                if (!safe.decoderConfig) {
                    safe = { decoderConfig: { codec: vc.codec, codedWidth: w, codedHeight: h, colorSpace: DEFAULT_COLOR_SPACE } };
                } else {
                    var dc = Object.assign({}, safe.decoderConfig);
                    if (dc.description) dc.description = sanitizeAvcC(dc.description); // Firefox #458
                    if (!dc.colorSpace) dc.colorSpace = DEFAULT_COLOR_SPACE;
                    safe = { decoderConfig: dc };
                }
            }
            var _ts = _chunkTimestamp(chunk), _dtsUs;
            if (_clock) _dtsUs = _clock.dts(_ts);   // the frames' own times — see «decode time» above
            else { _dtsUs = _videoDtsAccumUs; _videoDtsAccumUs += (chunk.duration > 0 ? chunk.duration : _frameDurFallbackUs); }
            try { muxVideo(chunk, safe, _ts, _ts - _dtsUs); }
            catch (e) { encErr = encErr || e; }
        },
        error: function (e) {
            if (gen !== encGen) return;   // a replaced encoder's late word is not about the current one
            if (!encErr || encErr.__syncClosed) encErr = (e instanceof Error ? e : new Error(String(e && e.message || e)));
        }
      }, hint);
      /* The very config the codec pick proved: one builder for the probe and
         every rung (encoderConfig). */
      enc.configure(encoderConfig(spec, vc.codec, hint, w, h, fps, vbr, o, hint !== WASM_HINT && isSoftwareRung(hint, vc)));
      return enc;
    }
    videoEncoder = makeVideoEncoder(hintOrder[hintIdx]);
    // Dead encoder → next hint. Returns the frame index to resume from, or -1
    // when the cascade is exhausted (the caller then throws a typed error).
    function rebuildVideoEncoder(atFrame) {
        if (hintIdx + 1 >= hintOrder.length) return -1;
        /* ── can the caller actually rewind? ────────────────────────────────
           The cascade resumes at `chunksOut`, i.e. it re-asks drawFrame for
           frames the dead encoder never muxed. That is free for a caller that
           RENDERS frame i on demand (slideshow, audio-to-video, reverse), and
           impossible for one that PULLS from a stream: /remove-text pulls from
           a one-shot back-pressure bridge, so a replayed index waits for a
           frame that will never be produced again — a permanent, silent hang.

           It has been survivable only by luck of distribution: 30 d of prod
           shows ~95% of rebuilds at frame 0, which is a configure-time death
           BEFORE any frame is pulled, and that resumes cleanly (rebuilt
           sessions complete at 46% vs 45% for clean ones — no measurable
           difference). The mid-stream ones (at_frame 9/38/1420) all landed on
           replayable callers. Encoding it as a fact instead of a coincidence:
           a puller declares canReplayFrames:false and gets an honest typed
           failure instead of a wait with no end. */
        if (o.canReplayFrames === false && chunksOut < framesPulled) return -1;
        var from = hintOrder[hintIdx];
        hintIdx++;
        rebuilds++;
        var reason = encErr ? (encErr.name + ': ' + (encErr.message || '')).slice(0, 120) : 'unknown';
        try { videoEncoder.close(); } catch (_) {}
        encErr = null;
        if (_clock) _clock.restart();   // a new encoder states its own clock
        videoEncoder = makeVideoEncoder(hintOrder[hintIdx]);
        try {
            window.__ae && window.__ae('feature_use', {
                slug: window.__TOOL_SLUG || 'unknown', f: 'cve_encoder_rebuilt',
                from: from, to: hintOrder[hintIdx], at_frame: atFrame, chunks: chunksOut, reason: reason
            });
        } catch (_) {}
        // Resume at the first frame the dead stream never emitted; a keyframe
        // opens the new stream so the muxer's GOP stays valid.
        return chunksOut;
    }

    var audioEncoder = null;
    if (hasAudio) {
        /* Through the AAC door, never a bare AudioEncoder: the chunks the
           muxer sees are raw access units with a guaranteed
           decoderConfig.description. Chromium 109–111 handed out ADTS frames
           and no description, and copied verbatim they gave an MP4 that every
           Media Foundation player (Windows Media Player, «Кино и ТВ») plays
           WITHOUT sound while FFmpeg-based players play it fine — report
           #6990, /slideshow-maker, 12.09.2026. Opus (webm) passes untouched. */
        await ensureAacAccessUnit();
        audioEncoder = window.AacAccessUnit.encoder({
            output: function (chunk, meta) {
                audioBytesOut += (chunk.byteLength || 0);
                try { muxer.addAudioChunk(chunk, meta || {}); }
                catch (e) { encErr = encErr || e; }
            },
            error: function (e) { encErr = encErr || (e instanceof Error ? e : new Error(String(e && e.message || e))); }
        }, { slug: window.__TOOL_SLUG });
        audioEncoder.configure({
            codec: spec.audioCodec, sampleRate: audioSR,
            numberOfChannels: audioCh, bitrate: audioBitrate
        });
    }

    /* Render surface — but first ask whether this machine's canvas → VideoFrame
       path carries pixels at all (see probeCapture above). */
    var capture = await pickCaptureSurface(vc.codec, hintOrder[0], w, h,
        { spec: spec, fps: fps, bitrate: vbr, o: o, software: hintOrder[0] !== WASM_HINT && isSoftwareRung(hintOrder[0], vc) });
    reportCapture(capture, vc.codec, w, h);
    if (!capture.surface) throw captureDeadError(capture);
    var _sf = makeSurface(w, h, capture.surface === 'cpu');
    var surface = _sf.canvas;
    var sctx = _sf.ctx;
    var frameDurUs = 1e6 / fps;
    /* Where an export's time goes — one `cve_pace` event per export, success or
       not. Refund request #48 (15.10.2026): an iPhone rendered a 2-min 1080p
       /audio-to-video export in 17.5 min where 48 other iPhone exports of the
       same build took 0.2–0.4 s per second of audio, and nothing recorded could
       say whether the page's drawing or the hardware encoder was the slow half.
       draw_ms — awaiting the caller's drawFrame; wait_ms — back-pressure on the
       encoder queue; the rest of wall_ms is frame wrapping, encode calls, yields
       and the audio. */
    var _paceT0 = _cvNow(), _paceDrawMs = 0, _paceWaitMs = 0, _paceOk = false;

    try {
        var audioFedUs = 0, audioDone = !chunkSrc;
        // Pull provider chunks until audio reaches `untilUs` (or the provider ends).
        // Called interleaved with the video loop, then drained to completion.
        async function pumpAudioChunks(untilUs) {
            while (!audioDone && audioFedUs < untilUs) {
                throwIfAborted();
                var chunk = await chunkSrc.next();
                if (!chunk || !chunk.length) { audioDone = true; break; }
                audioFedUs = encodeAudio(audioEncoder, chunk, audioCh, throwIfAborted, audioFedUs);
                // Backpressure — AudioData blocks queue in the encoder.
                while (audioEncoder && audioEncoder.encodeQueueSize > 24 && !encErr) {
                    throwIfAborted();                      // same reason as the video drain
                    await _yieldTick(4);
                }
            }
        }

        // ── video: paint → VideoFrame → encode ──
        var startAt = 0, forceKey = false, _nextKeyUs = 0, _nextPumpUs = 0;
        encodeLoop:
        for (;;) {
        for (var i = startAt; i < totalFrames; i++) {
            throwIfAborted();
            if (encErr) break;
            // Await drawFrame's return — synchronous callbacks resolve
            // immediately (await on a non-Promise is a no-op), but a caller
            // that needs to seek an HTMLVideoElement (bg-video looping),
            // decode a Blob, or otherwise prepare frame data asynchronously
            // can return a Promise here and the encoder will wait. This is
            // the hook audio-to-video's bg-video sync uses to land the right
            // (time % bgVideoDuration) frame for each export step instead
            // of whatever the <video> element happens to be displaying.
            var _pd = _cvNow();
            await o.drawFrame(sctx, i, frameTimeSec(i));
            _paceDrawMs += _cvNow() - _pd;
            if (i + 1 > framesPulled) framesPulled = i + 1;
            // Free-tier brand mark, stamped AFTER the caller's frame so it is
            // never painted over. One line here covers every tool on this
            // pipeline; which of them it applies to is config/tiers.php
            // `watermark_scope` (published by the layout as __WM_SCOPE), and
            // whether THIS user gets it is the runtime tier probe inside
            // __wmShould. A tool that stamps in its own drawFrame (the
            // /audio-to-video original) is unaffected: it is already in scope,
            // so both marks land in the same corner with the same text.
            stampWatermark(sctx, w, h);
            var _tsUs = ft ? Math.round(frameTimeSec(i) * 1e6) : Math.round(i * frameDurUs);
            var _durUs = ft ? frameLenUs(i) : Math.round(frameDurUs);
            var vf = new VideoFrame(surface, {
                timestamp: _tsUs,
                duration: _durUs
            });
            if (_clock) _clock.fed(_tsUs, _durUs);
            // keyframe every 2 s of the clock (a source's own times: by time, not index)
            var _key = forceKey || (ft ? (_tsUs >= _nextKeyUs) : ((i % (fps * 2)) === 0));
            if (ft && _key) _nextKeyUs = _tsUs + 2e6;
            // keyframe every 2 s — standard GOP, keeps the file seekable.
            /* A dead codec reports itself TWO ways and only one of them was
               handled. The `error` callback sets encErr and the loop checks it
               at the top — but the check and this call are separated by an
               `await drawFrame`, and a codec that dies inside that gap makes
               encode() throw SYNCHRONOUSLY ("Cannot call 'encode' on a closed
               codec"). That throw unwound past the whole hint cascade, so the
               export failed on hint #1 with a stack trace instead of retrying
               on software. Prod 30 d: 53 sessions on /remove-text-from-video,
               4 on /remove-object-from-video. Funnelling it into encErr puts
               both death reports on the same road. */
            try {
                videoEncoder.encode(vf, { keyFrame: _key });
            } catch (encThrow) {
                encErr = encErr || syncClosed(encThrow);
                try { vf.close(); } catch (_) {}
                break;
            }
            forceKey = false;
            vf.close();
            beat();
            // Chunked audio rides just behind the video clock (~1 s granularity)
            // so the muxer interleaves A/V instead of writing all-video-then-audio.
            if (chunkSrc && (ft ? (_tsUs >= _nextPumpUs) : (i % fps) === 0)) {
                if (ft) _nextPumpUs = _tsUs + 1e6;
                await pumpAudioChunks(ft ? _tsUs : i * frameDurUs);
            }
            // Backpressure — VideoFrames queued in the encoder each pin a
            // full surface; drain before the queue grows unbounded.
            if (videoEncoder.encodeQueueSize > 8) {
                // Back-pressure, not a wait for godot: an encoder that stops
                // draining and never fires `error` used to spin this loop for
                // the life of the tab. Abort and the stall kill-switch end it.
                var _pw = _cvNow();
                await waitEncoderQueue(videoEncoder, 4, function () {
                    return !!encErr || (kill && kill.stalled) || (signal && signal.aborted);
                });
                _paceWaitMs += _cvNow() - _pw;
                throwIfAborted();
            }
            if (o.onProgress && (i % 3 === 0)) o.onProgress((i / totalFrames) * 0.9);
            // Yield a macrotask now and then: VideoEncoder reports its death
            // through a POSTED error callback, and a short deck's loop can
            // otherwise finish before the news arrives (fixture 2026-08-27:
            // 10 frames, dead encoder, «success» with an empty 8 KB file).
            if ((i & 15) === 15) await _yieldTick(0);
        }
        if (!encErr) {
            // Let the encoder drain and any late error land before judging.
            try { await videoEncoder.flush(); } catch (fe) { encErr = encErr || fe; }
            await _yieldTick(0);
            if (!encErr && chunksOut === 0 && totalFrames > 0) {
                // A silent encoder: frames went in, nothing came out. Never
                // finalize that — it is a dead encoder wearing a success mask.
                encErr = new Error('Encoder produced no output');
                try { encErr.name = 'OperationError'; } catch (_) {}
            }
        }
        if (!encErr) break encodeLoop;
        // «closed» only — the reason is one posted task away (see encGen)
        if (encErr.__syncClosed) await _yieldTick(1);
        var resumeAt = rebuildVideoEncoder(i);
        if (resumeAt < 0) {
            var dead = encErr || new Error('encoder-failed');
            try { dead.encoderExhausted = true; dead.encoderHints = hintOrder.slice(); dead.encoderRebuilds = rebuilds; dead.encoderCodec = vc.codec; dead.encoderAtFrame = i; } catch (_) {}
            throw dead;
        }
        startAt = resumeAt;
        forceKey = true;
        }

        // ── audio ──
        if (hasAudio && ab) encodeAudio(audioEncoder, ab, audioCh, throwIfAborted, 0);
        if (chunkSrc) await pumpAudioChunks(Infinity);

        throwIfAborted();
        if (encErr) { encErr.encoderExhausted = (hintIdx + 1 >= hintOrder.length); throw encErr; }
        // video already flushed inside the cascade loop; audio here
        if (audioEncoder) await audioEncoder.flush();
        if (encErr) throw encErr;

        if (o.onProgress) o.onProgress(0.97);
        _paceOk = true;
        muxer.finalize();
        if (o.onProgress) o.onProgress(1);
        /* Two ways an export can finish «successfully» with a file nobody can
           watch, and neither one raises an error anywhere:

             1. the container loses writes — the encoder produced megabytes, the
                file holds kilobytes;
             2. the encoder was handed blank frames — the file honestly holds
                what it was given, which is nothing.

           Both shipped as `export_complete` and were counted as successes.
           Production, 30 days: three people received files of 11-16 kbit/s at
           720p. Measured signature of a solid-colour 60 s 1280x720 VP9 encode:
           11 kbit/s; a plain gradient backdrop is already 1390, and a blurry
           160 px stand-in 2744. So anything under ~1 % of the requested bitrate
           is not video, whichever of the two causes produced it.

           The encoder's own byte total tells the two apart, and it costs one
           addition per chunk. `emitted` is what the encoder made; the caller
           compares it with the file it is about to hand over. */
        var emitted = videoBytesOut + audioBytesOut;
        var expectedFloor = Math.max(1, Math.round(vbr * (o.durationSec || 0) / 8 * 0.01));
        function outputVerdict(fileBytes) {
            return {
                emitted: emitted, file: fileBytes, chunks: chunksOut,
                askedBitrate: vbr, floor: expectedFloor,
                // The encoder made almost nothing: it was fed blank frames.
                starved: emitted < expectedFloor,
                // The encoder made bytes the file does not contain: writes lost.
                lost: emitted > 0 && fileBytes < emitted * 0.5,
            };
        }

        if (opfsWritable) {
            // Flush the muxer's final writes to disk, then re-open as a
            // File. The File IS a Blob (it inherits from Blob) so the
            // caller's download/save path works unchanged — except that
            // URL.createObjectURL on a disk-backed File doesn't copy
            // bytes into the JS heap, which is the whole win.
            var file;
            /* Closing a PICKED file is work in proportion to its size: the browser
               scans the swap and moves it over the target — measured 14.10.2026
               with the real dialog, 17 s for 1 GB on an NVMe disk (the origin's own
               file closes at once). The stall guard judges SILENCE (floor 180 s),
               so the close keeps the heartbeat for as long as a file of this size
               may honestly take — a minute per GiB, two minutes at least — and no
               longer: a close that hangs is still caught. */
            var closeBeat = null;
            if (pickedLane) {
                var closeUntil = Date.now() + Math.max(120000, Math.ceil((lane.stats.end || 0) / 1073741824) * 60000);
                closeBeat = setInterval(function () {
                    if (Date.now() < closeUntil) beat(); else clearInterval(closeBeat);
                }, Math.min(5000, Math.max(250, Math.round(api.stallFloorMs / 8))));
            }
            try {
                file = await lane.finish();
            } catch (closeErr) {
                if (closeBeat) clearInterval(closeBeat);
                /* mp4-muxer's stream-target writes are fire-and-forget promises: if a
                   write REJECTS (QuotaExceededError), the stream just flips to
                   "errored", and the first visible symptom is close() throwing
                   "Cannot close a ERRORED writable stream", which masks the real
                   cause. With the room reserved up front this takes a file that
                   outgrew its reservation on a disk that filled meanwhile.
                   finish() has already dropped the temp file. */
                opfsWritable = null;
                throw diskWriteError(lane.stats.failed || closeErr, lane.picked);
            }
            if (closeBeat) clearInterval(closeBeat);
            opfsWritable = null;
            /* The file is on disk now, so this is the first moment the two
               numbers can be compared. A disk target that lost writes must not
               be handed on as a result — retrying in memory is cheap next to a
               user re-rendering a deck for three minutes to get another corpse. */
            var vd = outputVerdict(file.size);
            if (vd.lost || vd.starved) {
                var e = new Error(vd.lost ? 'output-truncated' : 'output-starved');
                e.outputVerdict = vd;
                throw e;
            }
            file.__cveVerdict = vd;
            /* The person's own file: it is the delivery, not a temp copy — the
               caller reads this mark instead of offering the same bytes again. */
            if (pickedLane) { try { file.__savedToPicked = true; } catch (_) {} return file; }
            /* No timer: the file lives while the page wants it — see release() above. */
            if (resultFiles) { try { resultFiles.set(file, { root: lane.root, name: lane.name }); } catch (_) {} }
            return file;
        }
        var memBlob = memTarget ? memTarget.finish(spec.mime) : new Blob([target.buffer], { type: spec.mime });
        memTarget = null;
        var vdMem = outputVerdict(memBlob.size);
        if (vdMem.starved) {
            /* In memory there is nothing to lose bytes to, so a file this small
               means the frames themselves were empty — the caller must not
               present it as a finished video. */
            var e2 = new Error('output-starved');
            e2.outputVerdict = vdMem;
            throw e2;
        }
        try { memBlob.__cveVerdict = vdMem; } catch (_) {}
        return memBlob;
    } finally {
        if (framesPulled >= fps) ae('cve_pace', Object.assign({
            slug: window.__TOOL_SLUG || 'unknown', ok: _paceOk ? 1 : 0,
            frames: framesPulled, total: totalFrames, w: w, h: h, fps: fps,
            wall_ms: Math.round(_cvNow() - _paceT0), draw_ms: Math.round(_paceDrawMs), wait_ms: Math.round(_paceWaitMs),
            hw: vc && vc.hardware ? 1 : 0, hint: hintOrder[hintIdx] || '', rebuilds: rebuilds,
            surface: capture && capture.surface || '', hidden: document.hidden ? 1 : 0
        }, (videoEncoder && typeof videoEncoder.stats === 'function') ? { wasm: videoEncoder.stats() } : {}));
        try { if (videoEncoder.state !== 'closed') videoEncoder.close(); } catch (_) {}
        try { if (audioEncoder && audioEncoder.state !== 'closed') audioEncoder.close(); } catch (_) {}
        // If encode threw and we have an open writable + temp file, abort
        // the writable and remove the partial file — otherwise stale
        // half-files accumulate in OPFS quota.
        if (opfsWritable && lane) lane.abort();
        // A failed encode must not keep its blocks: up to the whole file in RAM.
        if (memTarget) { try { memTarget.release(); } catch (_) {} }
    }
}

/* A write the disk refused, named as such. `diskFull` is what callers branch
   on — the room, not the memory, ran out, so lowering the resolution is not
   the remedy; encoding into memory is (support chat 647). The message keeps
   the `opfs-write-failed:` prefix older callers already match. */
function diskWriteError(cause, picked) {
    var msg = (cause && cause.message) || String(cause || 'stream-errored');
    var e = new Error('opfs-write-failed:' + msg);
    e.diskFull = !!(cause && (cause.name === 'QuotaExceededError' || cause.code === 22)) || /quota/i.test(msg);
    // The file the person picked refused the write — «there», not «the system disk».
    if (picked) e.picked = true;
    e.cause = cause;
    return e;
}

/* ───────────────── audio remux: the `no-audio-encoder` lane ─────────────────
 *
 * A browser can have the VIDEO half of WebCodecs and not the audio half —
 * iOS 16.4–18 shipped exactly that, Firefox still does. `isSupported()`
 * answers `no-audio-encoder` there, and until 2026-09-04 every consumer read
 * that as "no WebCodecs" and dropped the WHOLE export to the software lane.
 *
 * That trade is upside down. The expensive half — every frame of video — is
 * the half the device CAN do in hardware; what is missing is a few hundred
 * kilobytes of AAC. Measured on /slideshow-maker, 30 days: 114 sessions took
 * the software lane for this reason and 31 of them never finished (73 %
 * completion against 99.7 % on the fast lane) — the software lane there is a
 * REAL-TIME MediaRecorder capture plus a transcode, so a three-minute show
 * costs at least three minutes before the transcode even starts.
 *
 * So: encode the picture with WebCodecs, then hand FFmpeg the finished video
 * and the mixed soundtrack and let it copy the video through untouched
 * (`-c:v copy`) while encoding only the audio. No frame is decoded twice.
 *
 * Fails soft on purpose — the caller keeps its old fallback for the cases
 * this cannot answer (no FFmpeg on the page, a file too big to sit in MEMFS
 * three times over).
 *
 * ⚠️ /audio-to-video решило ту же задачу СВОИМ кодом в блейде
 * (`exportViaWebCodecsThenMuxAudio`, замер: 2016 с на программном лейне против
 * 185 с — 11×). Общая функция здесь появилась второй, поэтому и принимает оба
 * вида звука: готовый ФАЙЛ (случай a2v — одна дорожка, декодировать нечего) и
 * смикшированный AudioBuffer (случай слайдшоу — несколько дорожек плюс звук
 * видеофрагментов). Сводить a2v сюда — отдельная правка со своим замером. */
var REMUX_MAX_BYTES = 400 * 1024 * 1024;

function _cvNow() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }

function ae(f, payload) {
    try {
        if (window.__ae) window.__ae('feature_use', Object.assign({ f: f }, payload || {}));
    } catch (_) { }
}

function remuxSupportReason(reason) {
    return reason === 'no-audio-encoder';
}

async function wavFromAudioBuffer(buf) {
    if (!window.AudioPipeline || typeof window.AudioPipeline.audioBufferToWav !== 'function') {
        /* Тот же писатель WAV, что у всех остальных тулов (24/32 бита, один
           проход интерливинга) — вторая копия разошлась бы с ним молча. */
        var src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/audio-pipeline.js') : '/js/audio-pipeline.js';
        await loadScript(src);
    }
    if (!window.AudioPipeline || typeof window.AudioPipeline.audioBufferToWav !== 'function') {
        throw new Error('no-wav-writer');
    }
    return window.AudioPipeline.audioBufferToWav(buf);
}

/**
 * Вклеить звук в уже закодированное видео: `-c:v copy`, кодируется только
 * аудио. Возвращает новый Blob или null, если лейн недоступен — тогда
 * вызывающий остаётся при своём прежнем запасном пути.
 */
async function muxAudioInto(videoBlob, audio, opts) {
    opts = opts || {};
    var format = opts.format === 'webm' ? 'webm' : 'mp4';
    var spec = CONTAINERS[format];
    try {
        var isBlob = (typeof Blob !== 'undefined') && (audio instanceof Blob);
        if (!videoBlob || !audio || (!isBlob && !audio.length)) return null;
        if (!window.FFmpegProcessor || typeof window.FFmpegProcessor.processMulti !== 'function') {
            ae('cve_remux_skipped', { reason: 'no-ffmpeg', format: format });
            return null;
        }
        /* MEMFS держит вход и выход одновременно, поверх самого блоба в куче:
           три копии одного файла. Выше этого потолка честнее отдать вызывающего
           его прежнему лейну, чем убить вкладку на «Сохранить». */
        if (videoBlob.size > REMUX_MAX_BYTES) {
            ae('cve_remux_skipped', { reason: 'too-big', mb: Math.round(videoBlob.size / 1048576), format: format });
            return null;
        }
        var t0 = (window.performance && performance.now()) || 0;
        /* На странице, которая не подключила ffmpeg-processor.js тегом,
           `window.FFmpegProcessor` — ЗАГЛУШКА layout'а: она копит вызовы и
           через 15 с отвергает их «module never loaded». Доклейка звука на
           такой странице падала ВСЕГДА (найдено 25.09 на /eye-contact: каждый
           WebM/MKV выходил без звука). Настоящий модуль поднимает
           __ensureFFmpeg — примитив сам, а не каждый вызывающий. */
        if (window.FFmpegProcessor.__isStub && typeof window.__ensureFFmpeg === 'function') {
            try { await window.__ensureFFmpeg(); }
            catch (le) {
                ae('cve_remux_skipped', { reason: 'ffmpeg-load-failed', format: format, msg: String((le && le.message) || le).slice(0, 80) });
                return null;
            }
        }
        /* init() ЯВНО и с проверкой: processMulti зовёт его сам, но на отказ
           отвечает голым «FFmpeg not initialized» — без него первая же
           неудачная загрузка ядра выглядела как сломанная доклейка. */
        var ready = await window.FFmpegProcessor.init();
        if (!ready) { ae('cve_remux_skipped', { reason: 'ffmpeg-init-failed', format: format }); return null; }
        /* Звук приходит либо ГОТОВЫМ ФАЙЛОМ (одна дорожка — её и отдаём
           ffmpeg как есть), либо смикшированным буфером (слайдшоу мешает
           несколько дорожек и звук видеофрагментов — исходного файла там нет). */
        var aFile = isBlob
            ? new File([audio], 'audio.' + ((audio.name || 'a.bin').split('.').pop() || 'bin'), { type: audio.type || 'application/octet-stream' })
            : new File([await wavFromAudioBuffer(audio)], 'audio.wav', { type: 'audio/wav' });
        var vName = 'video.' + (format === 'webm' ? 'webm' : 'mp4');
        var vFile = new File([videoBlob], vName, { type: spec.mime });
        /* `-af apad -shortest` — ровно длина ВИДЕО, в обе стороны. Один
           `-shortest` режет по короткой дорожке, а значит слайдшоу, которое
           длиннее своей музыки, вышло бы обрезанным (проверено на стенде:
           1 с видео + 3 с звука → 1.3 с). `apad` тянет тишину до бесконечности,
           и обрезает уже видео. */
        var args = ['-map', '0:v:0', '-map', '1:a:0',
                    '-c:v', 'copy', '-c:a', format === 'webm' ? 'libopus' : 'aac',
                    '-b:a', String(Math.round((opts.audioBitrate || 192000) / 1000)) + 'k',
                    '-af', 'apad', '-shortest'];
        /* `-shortest` кончает по буферу кодировщика, а не по видео: звук выходил
           на 60–80 мс длиннее картинки (стенд video-editor-export-lanes.mjs, D2:
           12,16 с при видео 12,08). Вызывающий, который знает длину своего видео
           (`durationSec`), получает ровно её. */
        if (opts.durationSec > 0 && isFinite(opts.durationSec)) args.push('-t', (+opts.durationSec).toFixed(6));
        // moov в начале файла — иначе получатель по ссылке не может начать
        // проигрывать, пока не скачает всё.
        if (format === 'mp4') args.push('-movflags', '+faststart');
        if (opts.signal && opts.signal.aborted) return null;
        var res = await window.FFmpegProcessor.processMulti([vFile, aFile], args, 'muxed.' + (format === 'webm' ? 'webm' : 'mp4'));
        if (opts.signal && opts.signal.aborted) return null;
        if (!res || !res.blob || !res.blob.size) throw new Error('empty-remux');
        ae('cve_remux_ok', {
            format: format,
            ms: Math.round(((window.performance && performance.now()) || 0) - t0),
            inMB: Math.round(videoBlob.size / 1048576),
            outMB: Math.round(res.blob.size / 1048576),
        });
        /* The silent picture was a step: the clip is the muxed file now. Its disk file goes
           here, once, for every caller (callers fall back to the input only on null). */
        if (opts.keepInput !== true) release(videoBlob);
        return res.blob;
    } catch (e) {
        ae('cve_remux_failed', { format: format, reason: String((e && e.message) || e).slice(0, 90) });
        return null;
    }
}

/* ───────────────────── push-mode stream: openStream() ─────────────────────
 *
 * encode() PULLS: it asks drawFrame(i) for every index of a length it knows
 * in advance. That is the right shape for a slideshow or a waveform, and the
 * wrong one for a pipeline that PRODUCES frames one at a time from something
 * slow and open-ended — /video-colorize runs a neural network for 0.3–6 s per
 * frame and does not know where a segment ends until it ends it.
 *
 * Until 2026-09-05 that tool encoded its segments with libx264 inside
 * FFmpeg-WASM, ONE instance for the whole render. Measured on this engine
 * (tests/scratch/cvp-encoder-longevity-scratch.mjs, 720×400, 300-frame
 * segments): the instance grows ~30 MB per segment and never gives it back —
 * 755 → 2064 MB over 45 segments — and Firefox 155 trapped at segment ~39 with
 * «index out of bounds», 17.5 hours into a render, taking every frame with it.
 * A hardware encoder has no such arena: the same segments cost a flat RSS and
 * a fraction of the time (tests/scratch/cvp-webcodecs-segments-scratch.mjs).
 *
 * So this is the same VideoEncoder → mp4-muxer path as encode(), turned
 * inside out: the caller pushes frames, closes the stream when IT decides,
 * and gets a standalone playable MP4 (moov at the head) — small enough to
 * stay in an ArrayBuffer, independent enough to be stitched later with a
 * stream copy. Everything that made encode() survive the field is shared:
 * the codec pick with its lying-probe fallback, the Firefox avcC repair, the
 * B-frame-safe DTS accounting, the encoder-death cascade.
 *
 * ⚠️ The cascade needs frames to REPLAY. encode() re-asks drawFrame; a pusher
 * cannot be asked. So the stream keeps the last few frames it was handed as
 * JPEG (q 0.95 — a rebuild is rare and the loss is invisible next to the
 * encode itself) and replays them into the rebuilt encoder, keyframe first.
 * The ring is bounded by the back-pressure window plus a margin; frames older
 * than that have already come out of the encoder and cannot be lost.
 *
 *   const st = await CanvasVideoExport.openStream({ width, height, fps, format:'mp4', bitrate|qualityCrf, stallMs });
 *   await st.addFrame(canvas);      // any CanvasImageSource; awaited = back-pressure
 *   const r = await st.finish();    // { blob } — playable MP4 of everything pushed
 *   st.abort();                     // drop everything (also safe after finish)
 */
async function openStream(o) {
    o = o || {};
    if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') throw new Error('no-webcodecs');
    var spec = CONTAINERS[o.format || 'mp4'];
    if (!spec) throw new Error('unsupported-format:' + o.format);
    await ensureMuxer(spec);
    var MuxerNs = window[spec.muxerGlobal];
    if (!MuxerNs) throw new Error('muxer-not-loaded:' + spec.muxerGlobal);
    var w = o.width, h = o.height, fps = o.fps || 24;
    if (!(w > 0 && h > 0)) throw new Error('bad-dims');
    var vbr = o.bitrate || videoBitrateFor(w, h, fps, o.qualityCrf);
    var vc = await pickVideoCodec(spec, w, h, vbr, fps, !!o.h264Only, null, o);
    if (!vc) throw new Error('no-encoder');
    var _isAndroid = /Android/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
    var hintOrder = vc.wasm ? [WASM_HINT] : _isAndroid
        ? ['prefer-software', 'no-preference']
        : (vc.hardware ? ['prefer-hardware', 'no-preference', 'prefer-software'] : ['no-preference', 'prefer-software']);
    /* The rung below a native H.264 that dies mid-stream (2026-10-29) — see encode(). The
       ladder above ends at `prefer-software`, which on Yandex Browser does not
       exist: prod, slideshow, October — 34 sessions where every native rung
       died (Android HW «Encoding error», desktop HW dead and «Failed to create
       OpenH264»), 14 of them never got a file. x264 continues from the first
       frame the dead encoder never emitted; its parameter sets differ, and
       __muxVideoTrack carries them in-band on its keyframes. Above 1080p only
       where it can run threaded (WasmH264Encoder.suits). */
    if (vc && !vc.wasm && /^avc1\./i.test(vc.codec || '') && spec.muxerGlobal === 'Mp4Muxer' && await wasmH264Suits(w, h)) hintOrder.push(WASM_HINT);
    var hintIdx = 0, rebuilds = 0;
    var target = new MuxerNs.ArrayBufferTarget();
    var muxerCfg = {
        target: target,
        video: { codec: muxVideoCodecFor(spec, vc), width: w, height: h, frameRate: 120000 },
        firstTimestampBehavior: 'offset'
    };
    Object.assign(muxerCfg, spec.muxerExtra);
    var muxer = new MuxerNs.Muxer(muxerCfg);
    // Parameter sets that change mid-stream travel in-band (report #8247) — see __muxVideoTrack.
    var muxVideo = window.__muxVideoTrack ? window.__muxVideoTrack(muxer) : function (c, m, t, o) { return muxer.addVideoChunk(c, m, t, o); };
    var DEFAULT_COLOR_SPACE = { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };
    var frameDurUs = 1e6 / fps;
    var encErr = null, chunksOut = 0, frames = 0, firstVideoChunk = true, forceKey = false, closed = false;
    var _dtsAccumUs = 0;
    /* Which frame a chunk is, and when it is decoded — the same clock as encode()
       (window.__encoderClock): Firefox stamps a chunk with the END of its frame, and
       the running sum of rounded durations ran ahead of the frames' times (see
       «decode time» in encode()). This lane had neither until 04.11.2026. */
    var _clock = window.__encoderClock ? window.__encoderClock({ onEnd: function () {
        ae('cve_chunk_end_stamped', { slug: window.__TOOL_SLUG || 'unknown', codec: String(vc && vc.codec || '').slice(0, 20), lane: 'stream' });
    } }) : null;
    var RING = 32;                    // > back-pressure window (8) + hardware lag margin
    var ring = [];                    // [{i, blob, p}] — the last RING frames pushed, as JPEG
    var capture = await pickCaptureSurface(vc.codec, hintOrder[0], w, h,
        { spec: spec, fps: fps, bitrate: vbr, o: o, software: hintOrder[0] !== WASM_HINT && isSoftwareRung(hintOrder[0], vc) });
    reportCapture(capture, vc.codec, w, h);
    if (!capture.surface) throw captureDeadError(capture);
    var _sf = makeSurface(w, h, capture.surface === 'cpu');
    var surface = _sf.canvas;
    var sctx = _sf.ctx;

    var encGen = 0;   // see encode(): an encoder's death belongs to that encoder
    function makeEncoder(hint) {
        var gen = ++encGen;
        var enc = newVideoEncoder({
            output: function (chunk, meta) {
                var safe = meta || {};
                chunksOut++;
                if (firstVideoChunk) {
                    firstVideoChunk = false;
                    if (!safe.decoderConfig) {
                        safe = { decoderConfig: { codec: vc.codec, codedWidth: w, codedHeight: h, colorSpace: DEFAULT_COLOR_SPACE } };
                    } else {
                        var dc = Object.assign({}, safe.decoderConfig);
                        if (dc.description) dc.description = sanitizeAvcC(dc.description);   // Firefox #458
                        if (!dc.colorSpace) dc.colorSpace = DEFAULT_COLOR_SPACE;
                        safe = { decoderConfig: dc };
                    }
                }
                var _ts = _clock ? _clock.ts(chunk) : chunk.timestamp, _dtsUs;
                if (_clock) _dtsUs = _clock.dts(_ts);
                else { _dtsUs = _dtsAccumUs; _dtsAccumUs += (chunk.duration > 0 ? chunk.duration : frameDurUs); }
                try { muxVideo(chunk, safe, _ts, _ts - _dtsUs); }
                catch (e) { encErr = encErr || e; }
            },
            error: function (e) {
                if (gen !== encGen) return;
                if (!encErr || encErr.__syncClosed) encErr = (e instanceof Error ? e : new Error(String(e && e.message || e)));
            }
        }, hint);
        // The config the codec pick proved — see encoderConfig.
        enc.configure(encoderConfig(spec, vc.codec, hint, w, h, fps, vbr, o, hint !== WASM_HINT && isSoftwareRung(hint, vc)));
        return enc;
    }
    var encoder = makeEncoder(hintOrder[hintIdx]);

    function toErr(e) { return e instanceof Error ? e : new Error(String(e && e.message || e)); }
    function feed(source, idx, key) {
        var _tsUs = Math.round(idx * frameDurUs), _durUs = Math.round(frameDurUs);
        var vf = new VideoFrame(source, { timestamp: _tsUs, duration: _durUs });
        if (_clock) _clock.fed(_tsUs, _durUs);
        try { encoder.encode(vf, { keyFrame: !!key }); }
        catch (encThrow) { if (!encErr) { encErr = toErr(encThrow); try { encErr.__syncClosed = true; } catch (_) {} } }   // weak: see encGen in encode()
        vf.close();
    }
    // Back-pressure with a pulse. An encoder that stops draining and never
    // fires `error` is the failure encode() already knew («an encoder that
    // stops draining … used to spin this loop for the life of the tab»); in
    // push mode it is worse, because nothing outside can tell «slow frame»
    // from «dead codec» — measured on the REAL Edge run of 05.09.2026: the
    // render sat at frame 75 of 7304 until the tool's 90-s watchdog shot it.
    // So silence has a budget here: no new chunk for STALL_MS while the queue
    // is full → the codec is declared dead and the cascade rebuilds it,
    // replaying the frames it swallowed from the ring.
    var STALL_MS = (typeof o.stallMs === 'number' && o.stallMs > 0) ? o.stallMs : 20000;
    async function drain(limit) {
        var since = _nowMs(), seen = chunksOut;
        while (encoder && encoder.encodeQueueSize > limit && !encErr) {
            await _yieldTick(4);
            if (chunksOut !== seen) { seen = chunksOut; since = _nowMs(); }
            else if (_nowMs() - since > STALL_MS) {
                encErr = new Error('encoder-stalled:' + encoder.encodeQueueSize + ' queued, no output for ' + Math.round(STALL_MS / 1000) + ' s');
                try { encErr.name = 'OperationError'; } catch (_) {}
            }
        }
    }
    function _nowMs() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }
    // flush() on a dead codec can hang forever; give it the same budget.
    function flushWithBudget() {
        return Promise.race([
            encoder.flush(),
            new Promise(function (_, rej) { setTimeout(function () { var e = new Error('encoder-stalled:flush'); try { e.name = 'OperationError'; } catch (_) {} rej(e); }, STALL_MS); })
        ]);
    }
    async function settleRing() {
        for (var k = 0; k < ring.length; k++) { if (!ring[k].blob) { try { ring[k].blob = await ring[k].p; } catch (_) {} } }
        ring = ring.filter(function (r) { return !!r.blob; });
    }
    // Encoder died → next hint, replay what the dead stream never emitted.
    async function rebuild() {
        if (encErr && encErr.__syncClosed) await _yieldTick(1);   // the reason, not «closed» (see encGen in encode())
        if (hintIdx + 1 >= hintOrder.length) {
            var dead = encErr || new Error('encoder-failed');
            try { dead.encoderExhausted = true; dead.encoderHints = hintOrder.slice(); dead.encoderRebuilds = rebuilds; } catch (_) {}
            throw dead;
        }
        var from = hintOrder[hintIdx];
        var reason = encErr ? (encErr.name + ': ' + (encErr.message || '')).slice(0, 120) : 'unknown';
        hintIdx++; rebuilds++;
        try { encoder.close(); } catch (_) {}
        encErr = null;
        encoder = makeEncoder(hintOrder[hintIdx]);
        if (_clock) _clock.restart();   // the new instance states its own clock; the replay feeds it again
        try {
            window.__ae && window.__ae('feature_use', {
                slug: window.__TOOL_SLUG || 'unknown', f: 'cve_stream_rebuilt',
                from: from, to: hintOrder[hintIdx], at_frame: frames, chunks: chunksOut, reason: reason
            });
        } catch (_) {}
        // Frames [chunksOut, frames) went into the dead encoder and never came out.
        await settleRing();
        var lost = ring.filter(function (r) { return r.i >= chunksOut; });
        if (frames > chunksOut && lost.length !== frames - chunksOut) {
            var gap = new Error('stream-replay-gap:' + (frames - chunksOut - lost.length));
            gap.encoderExhausted = true;
            throw gap;
        }
        var first = true;
        for (var k = 0; k < lost.length; k++) {
            var bmp = await createImageBitmap(lost[k].blob);
            sctx.drawImage(bmp, 0, 0, w, h);
            if (bmp.close) bmp.close();
            feed(surface, lost[k].i, first);
            first = false;
            await drain(8);
        }
        forceKey = lost.length === 0;   // nothing replayed → the next pushed frame opens the new stream
    }
    function remember(source, idx) {
        // JPEG, off the hot path: the blob settles before any rebuild can need
        // it (a rebuild awaits the ring first).
        try {
            sctx.drawImage(source, 0, 0, w, h);
            var p = (typeof surface.convertToBlob === 'function')
                ? surface.convertToBlob({ type: 'image/jpeg', quality: 0.95 })
                : new Promise(function (res) { surface.toBlob(res, 'image/jpeg', 0.95); });
            var entry = { i: idx, blob: null, p: p };
            ring.push(entry);
            if (ring.length > RING) ring.shift();
            p.then(function (b) { entry.blob = b; }, function () {});
        } catch (_) {}
    }

    return {
        addFrame: async function (source) {
            if (closed) throw new Error('stream-closed');
            if (encErr) await rebuild();
            remember(source, frames);
            feed(source, frames, forceKey || (frames % (fps * 2)) === 0);
            forceKey = false;
            frames++;
            beat();
            if (encoder.encodeQueueSize > 8) await drain(4);
            if ((frames & 15) === 15) await _yieldTick(0);
            if (encErr) await rebuild();
        },
        finish: async function () {
            if (closed) throw new Error('stream-closed');
            closed = true;
            for (var attempt = 0; attempt < hintOrder.length; attempt++) {
                if (!encErr) { try { await flushWithBudget(); } catch (fe) { encErr = encErr || toErr(fe); } }
                await _yieldTick(0);
                if (!encErr && chunksOut === 0 && frames > 0) {
                    // A silent encoder: frames went in, nothing came out. Never
                    // finalize that — it is a dead encoder wearing a success mask.
                    encErr = new Error('Encoder produced no output');
                    try { encErr.name = 'OperationError'; } catch (_) {}
                }
                if (!encErr) break;
                await rebuild();
            }
            if (encErr) throw encErr;
            try { encoder.close(); } catch (_) {}
            muxer.finalize();
            ring = [];
            var _outBlob = await carryTagsInto(new Blob([target.buffer], { type: spec.mime }), o);
            return { blob: _outBlob, frames: frames, chunks: chunksOut, rebuilds: rebuilds, codec: vc.codec, hardware: vc.hardware };
        },
        abort: function () {
            closed = true; ring = [];
            try { if (encoder && encoder.state !== 'closed') encoder.close(); } catch (_) {}
        },
        get frames() { return frames; }
    };
}

/* Работа движка учитывается в window.__execWork — см. шапку
   _execWorkAdd в ffmpeg-processor.js (один контракт на все движки; сторож —
   ExecWorkIsMeteredTest). encode() — один вызов; openStream() — задание от
   открытия потока до finish(), потому что кадры в него кладут по одному и
   ждут ровно столько. */
function __execWorkAdd(t0) {
    try { var w = window.__execWork; if (w) { w.ms += Date.now() - t0; w.n++; } } catch (_) {}
}
function __timedExec(fn) {
    return async function () {
        var t0 = Date.now();
        try { return await fn.apply(this, arguments); } finally { __execWorkAdd(t0); }
    };
}
function __timedStream(open) {
    return async function () {
        var t0 = Date.now();
        var st = await open.apply(this, arguments);
        var finish = st.finish;
        st.finish = async function () {
            try { return await finish.apply(st, arguments); } finally { __execWorkAdd(t0); }
        };
        return st;
    };
}

/* Fetch the fast lane's helpers BEFORE the press (the muxer for the container,
   the AAC door for mp4). Comment #1937, 17.10.2026: on a failing mobile network
   the 3 KB AAC helper missed both of its attempts at export time and a tablet
   that encodes H.264 fine fell to the VP9 + in-browser conversion lane. A tool
   calls this when its input is in; failures are swallowed — loadOnce() never
   caches a refusal, so the export asks again on its own. */
function warm(format) {
    var spec = CONTAINERS[format === 'webm' ? 'webm' : 'mp4'];
    var jobs = [ensureMuxer(spec)];
    if (spec.muxerAudioCodec === 'aac') jobs.push(ensureAacAccessUnit());
    return Promise.all(jobs).then(function () { return true; }, function () { return false; });
}

/* Can this machine make H.264 at this size, and how? 'native' — a WebCodecs
   encoder encoded a real frame (any hint); 'wasm' — only our WasmH264Encoder
   did; null — neither. The question a caller asks BEFORE choosing a lane that
   exists only because H.264 might be missing (the slideshow's VP9 + FFmpeg
   transcode): with the wasm rung that lane is for browsers without
   WebAssembly workers, not for every Yandex desktop without a GPU encoder. */
async function h264Lane(width, height) {
    var profiles = profilesFor(CONTAINERS.mp4, width, height);
    for (var i = 0; i < profiles.length; i++) {
        if (await probeEncoder(profiles[i], null, width, height)) return 'native';
        if (await probeEncoder(profiles[i], 'prefer-hardware', width, height)) return 'native';
    }
    if (await wasmH264Suits(width, height) && await probeEncoder(profiles[0], WASM_HINT, width, height)) return 'wasm';
    return null;
}

var api = window.CanvasVideoExport = {
    isSupported: isSupported,
    warm: warm,
    probeEncoder: probeEncoder,
    h264Lane: h264Lane,
    encode: __timedExec(encode),
    // The tool is done with a result (a new one replaced it): its disk file goes now.
    release: release,
    // Silence threshold for the stall guard (ms). Writable on purpose: it is the
    // single source for this lane — recon-pipeline reads it for its own wider
    // guard — and tests lower it to trip the watchdog in bounded time.
    stallFloorMs: STALL_FLOOR_MS,
    // Asked at the moment of use: the block target lives in the core bundle, which may load after this file.
    get memoryTargetCeiling() { return memoryCeiling(); },
    // Output-size estimate (bytes) matching encode()'s own bitrate mapping — for
    // callers' pre-flight checks (OPFS quota / WASM-mux caps) BEFORE a long encode.
    estimateBytes: function (w, h, fps, crf, durationSec, withAudio) {
        return ((videoBitrateFor(w, h, fps, crf) + (withAudio ? 192000 : 0)) / 8) * (durationSec || 0);
    },
    // Video via WebCodecs + audio glued on by FFmpeg — the lane for browsers
    // that have VideoEncoder and no AudioEncoder (see the block above).
    muxAudioInto: muxAudioInto,
    // Push-mode stream for producers that make frames one at a time (see openStream).
    openStream: __timedStream(openStream),
    canRemuxAudioFor: remuxSupportReason,
    remuxMaxBytes: REMUX_MAX_BYTES,
    videoBitrateFor: videoBitrateFor,   // exported for tests
    swRungBps: swRungBps,               // exported for tests
    isSoftwareRung: isSoftwareRung,     // exported for tests
    profilesFor: profilesFor,           // exported for tests (H.264 level vs frame size)
    _loadOnce: loadOnce,                // exported for tests (tests/js/cve-helper-load-retries.test.mjs)
    version: '1.7.1'
};

// Back-compat alias — audio-to-video shipped against `A2VWebCodecsExport`
// before this module was promoted to a shared canvas→video primitive.
// Keep the old name pointing at the same object so a2v's JS is untouched.
window.A2VWebCodecsExport = window.CanvasVideoExport;

})();
